// Self-contained client for billing-service (SKU catalog).
// Mirrors hardwareApi.ts — same shared `platform` cluster, different base path.
//
// The catalog is the SINGLE definition of a price. Nothing in the UI invents
// an amount; it reads them from here.

// The one formatter every other money figure on the Finance tab goes through
// — `describeDiscount` below must not hand-roll a second one that drifts from
// it (no thousands separator, say: `$1500.00` beside `$1,500.00`).
import { money } from '../components/finance/financeFormat';
// A RUNTIME value, not a type. The trial imports (`PLAN_TRIAL_DAY_OPTIONS`,
// `trialDaysFor`, `MAX_TRIAL_DAYS`) stood beside it until trials were retired
// on 2026-08-28.
import { applyDiscount } from '@platform/shared-types';
import type {
  DealDeviceTerm, DeviceCoverMode, DeviceDiscountTarget, PriceIntro,
  DealOfferTerm, OfferTarget, OfferCoverMode,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveBillingHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/billing-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/billing`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/billing`;
  return `https://platform.api.dev.${apex}/billing`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_BILLING_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveBillingHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* non-JSON */ }
    throw new Error(detail || `billing ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}


export interface BillingDriftEntry { packageId: string; skuId: string }

/**
 * A package, named and priced for display.
 *
 * `name` falls back to the package id when the packages table has no name, so
 * it is always safe to render — but it is never an invented name.
 */
export interface DriftPackageDisplay {
  packageId: string;
  skuId?: string;
  name: string;
  /** The stored name carried stray whitespace, which the server trimmed. */
  nameNeedsCleanup?: boolean;
  /** A lucide icon name. Often absent — most packages have never had one set. */
  icon?: string;
  color?: string;
  colorType?: string;
  amountCents?: number;
  interval?: string;
}

export interface BillingDriftTenant {
  tenantId: string;
  businessName?: string;
  /** Enabled for the tenant, but nothing bills for it. The revenue leak. */
  entitledNotBilled: BillingDriftEntry[];
  /** Billed for, but the tenant cannot use it. */
  billedNotEntitled: BillingDriftEntry[];
  /** Enabled packages with no catalog SKU — a catalog gap, not billing drift. */
  notInCatalog: string[];
  /** The same three lists, named and priced. Absent on a report from an older sweep. */
  display?: {
    entitledNotBilled: DriftPackageDisplay[];
    billedNotEntitled: DriftPackageDisplay[];
    notInCatalog: DriftPackageDisplay[];
  };
  unbilledMonthlyCents?: number;
  overbilledMonthlyCents?: number;
}

export interface BillingDriftReport {
  scannedAt: number | null;
  tenantsScanned: number;
  tenantsWithDrift: number;
  findings: BillingDriftTenant[];
  unreadable: Array<{ tenantId: string; reason: string }>;
  /**
   * Tenants with enabled packages but no plan assignment AT ALL. Deliberately
   * separate from `findings`: every package they have looks unbilled, so
   * folding them in would bury the tenant who has a priced plan missing a
   * package. They need pricing, not an amendment.
   */
  neverPriced: Array<{
    tenantId: string;
    businessName?: string;
    enabledPackages: number;
    display?: DriftPackageDisplay[];
    unpricedMonthlyCents?: number;
  }>;
  totals?: { unbilledMonthlyCents: number; overbilledMonthlyCents: number };
  /** Packages whose stored name carries stray whitespace — a data problem worth showing. */
  namesNeedingCleanup?: Array<{ packageId: string; name: string }>;
  /** TRUE means no sweep has ever run — which must not render as "all clear". */
  neverRun: boolean;
}

// ---------------------------------------------------------------------------
// The per-tenant drill-down — entitlement vs plan vs LIVE Stripe.
// ---------------------------------------------------------------------------

export type DriftRowVerdict =
  | 'aligned' | 'unbilled' | 'overbilled' | 'planned_not_charged' | 'charged_off_plan';

export interface DriftReconciledRow extends DriftPackageDisplay {
  enabled: boolean;
  onPlan: boolean;
  onStripe: boolean;
  stripeAmountCents?: number;
  /** Stripe charges a different amount than the catalog lists — usually a promotion. */
  pricedDifferently?: boolean;
  verdict: DriftRowVerdict;
}

export interface DriftStripeLine {
  itemId: string;
  priceId: string;
  amountCents: number;
  quantity: number;
  interval?: string;
  skuId?: string;
  managed: boolean;
}

export interface DriftLocation {
  locationId: string;
  name?: string;
  subscriptionId?: string;
  /** Why there is no Stripe comparison. An error and "never onboarded" differ. */
  stripeUnavailable?: string;
  /** FALSE means the assignment recorded no per-location placement. */
  placementRecorded: boolean;
  rows: DriftReconciledRow[];
  otherLines: DriftStripeLine[];
  /**
   * The LIST price this location's lines add up to.
   *
   * ⚠️ NOT what the merchant pays, and it must never be shown as such.
   * Promotions live on the subscription SCHEDULE's phases, not on item amounts,
   * so every offer is stripped out of this figure. For money on a screen use
   * `nextInvoiceCents`.
   */
  stripeMonthlyCents: number;
  /**
   * WHAT STRIPE WILL ACTUALLY TAKE NEXT — the upcoming invoice total, offers
   * and prorations included. Absent when it could not be read; never zero for
   * an unreadable one, because a location whose invoice failed to load has not
   * been shown to cost nothing.
   *
   * NOT necessarily a monthly figure — a mid-cycle read includes prorations —
   * so it is labelled "next invoice", never "/mo".
   */
  nextInvoiceCents?: number;
  /** When Stripe intends to take it, unix seconds. */
  nextInvoiceAt?: number;
  /** Why there is no next-invoice figure, when there is none. */
  nextInvoiceUnavailable?: string;
  unbilledMonthlyCents: number;
  overbilledMonthlyCents: number;
}

export interface BillingDriftDetail {
  tenantId: string;
  businessName?: string;
  /** FALSE means the tenant has no business name at all — a gap worth flagging. */
  hasBusinessName: boolean;
  assignment: { assignmentId: string; status: string; effectiveFrom: number } | null;
  hasAssignment: boolean;
  enabledPackages: DriftPackageDisplay[];
  locations: DriftLocation[];
  noLocations: boolean;
  totals: { unbilledMonthlyCents: number; overbilledMonthlyCents: number; stripeMonthlyCents: number };
  readAt: number;
}

export interface BillingSkuRow {
  skuId: string;
  type: 'plan' | 'package' | 'device_finance' | 'token_pack';
  scope: 'tenant' | 'location';
  name: string;
  description?: string;
  amountCents: number;
  interval: 'month' | 'year' | 'once';
  termMonths?: number;
  /** Hardware link — exactly one of these is set on a device SKU. */
  modelId?: string;
  bundleId?: string;
  /**
   * SOFTWARE LINK — the package this SKU sells, on `type: 'package'` rows.
   *
   * Already stored and already read server-side (`packageSkuIdByPackageId` in
   * billing-service builds its whole map from it); the type simply never named
   * it. It is the only route from a `sku_pkg_…` on a Stripe subscription item
   * back to the package's own name, icon and colour.
   */
  packageId?: string;
  saleMode?: 'outright' | 'installment' | 'lease' | 'rental' | 'included';
  /**
   * Financing-wizard fields (wave 3) — all optional/additive. An offer made
   * through the old bare Offers-tab editor has none of these; render their
   * absence as "not set", never as zero.
   */
  /** Offer-level commission plan override — tier one of the cascade. Absent = inherit. */
  commissionPlanId?: string;
  resellerPriceCents?: number;
  resellerMonthlyCents?: number;
  /** Rent-option + wizard fields (additive — absent on offers that predate them). */
  rentalKind?: 'open_ended' | 'fixed_term';
  minCommitmentMonths?: number;
  depositCents?: number;
  downPaymentCents?: number;
  affiliateCommission?: { mode: 'inherit_deal' | 'custom_percent' | 'none'; percent?: number };
  agreementTemplateId?: string;
  agreementTemplateVersion?: number;
  warrantyMonths?: number;
  earlyPayoff?: 'remaining_no_penalty';
  defaultPolicy?: { graceDays: number; enforcement: 'suspend_all' | 'suspend_feature' | 'collections_only' };
  /** Top-level enforcement (required on the stored row; `defaultPolicy.enforcement` is the per-agreement default). */
  enforcement?: 'suspend_all' | 'suspend_feature' | 'collections_only';
  status: 'active' | 'deprecated';
  displayOrder: number;
  /** Whether this SKU already has a Stripe price for the running stage. Server-computed — never re-derive this in the UI. */
  hasPrice: boolean;
  /**
   * Whether a Stripe price is EVER expected for this SKU. Since spec §13/B2
   * only `device_finance` SKUs carry one — software is billed as a single
   * managed line per location at the net we compute — so `hasPrice: false` on a
   * package or plan is NORMAL, not a gap to flag or "fix".
   *
   * Server-computed for exactly the reason `hasPrice` is: the browser must
   * never re-derive which SKU types may reach Stripe. Optional so a response
   * from a billing-service that predates the field still parses; treat
   * `undefined` as "unknown", never as `false`.
   */
  priceable?: boolean;
  /**
   * PROVENANCE (2026-08-27 financing-policy spec) — stamped by
   * `catalog.upsertSku` from the admin's token, never sent by the client.
   * All optional: a SKU minted before this shipped carries none of them, so
   * render their absence as "unknown", never as blank-but-present.
   */
  createdAt?: number;
  updatedAt?: number;
  createdBy?: string;
  updatedBy?: string;
  history?: Array<{
    at: number;
    by: string;
    action: 'created' | 'updated' | 'retired' | 'reoffered';
    changes?: Array<{ field: string; from?: string; to?: string }>;
  }>;
}

export interface GeneratePriceResult {
  skuId: string;
  priceId: string;
  created: boolean;
  stage: string;
  /**
   * TRUE when the server declined to price this SKU because its type never
   * carries a Stripe price (§13/B2). The call still returns 200 — it is a
   * legitimate answer, not a failure — so a caller that treats any 200 as
   * "price generated" will tell the operator something untrue. Always branch
   * on this before reporting success.
   */
  skipped?: boolean;
  /** The server's own explanation when `skipped` — show it verbatim. */
  reason?: string;
}

/**
 * Why one SKU was skipped by the bulk sync.
 *  - `not_priceable`  — its type never gets a Stripe price (§13/B2). Permanent.
 *  - `already_priced` — it has a price for this stage already. Nothing to do.
 *  - `deprecated`     — the SKU is not active.
 *
 * Branch on `code`, never on `reason`: the prose is for the operator.
 */
export type PriceSkipCode = 'not_priceable' | 'already_priced' | 'deprecated';

export interface SyncPricesResult {
  created: string[];
  /** Every skipped skuId. `skippedDetail` says why, one entry per id. */
  skipped: string[];
  /** Optional so an older billing-service still parses; treat `undefined` as "no detail available". */
  skippedDetail?: { skuId: string; reason: string; code: PriceSkipCode }[];
  failed: { skuId: string; reason: string }[];
}

export interface SyncError {
  /** Present when the failure is attributable to one package. */
  packageId?: string;
  locationId?: string;
  reason: string;
}

export interface SyncSubscriptionResult {
  /** True only when the live subscriptions now match the active plan assignment. */
  synced: boolean;
  added: number;
  removed: number;
  repriced: number;
  unchanged: number;
  /**
   * Set ONLY for the D12 no-plan case — every tenant today. Its presence means
   * nothing was touched; render it as information, never as an error.
   */
  skipped?: 'no-plan-assignment';
  errors: SyncError[];
  assignmentId?: string;
}

// ---------------------------------------------------------------------------
// PLAN ASSIGNMENTS — the internal record of what a tenant was SOLD (spec §5).
//
// Since spec §13/B1 Stripe no longer sees packages at all: software is ONE
// managed line per location at the net OUR arithmetic produced. That makes the
// PlanAssignment the only place the commercial structure exists — which
// packages, at which discount, and how the final number was arrived at — and
// `composeSubscription` (server-side, one pure function) the only thing that
// turns it into money. NOTHING in this file recomputes an amount: every number
// the builder shows comes back from `POST …/preview`.
// ---------------------------------------------------------------------------

export interface PlanDiscount {
  /**
   * EXACTLY ONE of `percentOff` / `amountOffCents` is ever set. `DiscountSchema`
   * refuses both ("exactly one of percentOff or amountOffCents is required") and
   * refuses a percentage outside 0–100. Build these with `toDiscount()`, which
   * takes a single mode and therefore cannot express "both".
   */
  percentOff?: number;
  amountOffCents?: number;
  reason?: string;
}

export type PlanAssignmentStatus = 'draft' | 'active' | 'superseded';

export interface PlanAssignmentRow {
  tenantId: string;
  assignmentId: string;
  /**
   * KEYS into the server's SKU map, not skuIds-only: a `package` SKU is keyed by
   * its `packageId`, and a `plan` SKU (or a package SKU with a blank packageId)
   * by its `skuId`. `packageKeyOf()` derives the right one from a catalog row.
   */
  planPackageIds: string[];
  discounts: { perPackage: Record<string, PlanDiscount>; overall?: PlanDiscount };
  /** Explicit `locationId -> package keys billed there`. Absent = the scope rule. */
  locationPackageIds?: Record<string, string[]>;
  status: PlanAssignmentStatus;
  effectiveFrom: number;
  supersedesAssignmentId?: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface ComposedLineItem {
  packageId: string;
  /** Always '' for a package line since §13/B3 — packages carry no Stripe price. */
  priceId: string;
  listCents: number;
  /** Worth after EVERY reduction, including this line's share of the overall discount. */
  netCents: number;
  discount?: PlanDiscount;
  /** This line's share of the overall discount, ALREADY inside `netCents`. */
  overallDiscountCents?: number;
}

export interface ComposedLocation {
  locationId: string;
  items: ComposedLineItem[];
}

export interface ComposeError {
  packageId: string;
  reason: string;
}

export type ComposeWarningCode =
  'no_locations' | 'free_package' | 'empty_plan' | 'fully_discounted' | 'placed_nowhere'
  | 'trial_at_creation_only'
  /**
   * A location that exists on this tenant but is billed NOTHING by this plan,
   * while the plan bills something elsewhere (spec §14/C2). It is the signature
   * of the placement leak: a store opened AFTER the assignment was written is
   * absent from every `locationPackageIds` list and bills nothing, silently,
   * for as long as nobody looks.
   */
  | 'location_bills_nothing';

export interface ComposeWarning {
  code: ComposeWarningCode;
  message: string;
  packageId?: string;
  /** Set by `location_bills_nothing`, which is about a store, not a package. */
  locationId?: string;
}

// ---------------------------------------------------------------------------
// BEFORE -> AFTER (spec §14/C2)
//
// Nobody's bill changes without a human seeing both numbers — and the "before"
// half of that comparison is COMPOSED BY THE SERVER from the assignment
// actually in force, never reassembled here from whatever the screen happened
// to load. Ask for it with `{ compareToActive: true }` on a preview; it arrives
// on an activation unasked, because by then it is the record of what was done.
// ---------------------------------------------------------------------------

export type PackageChangeKind = 'added' | 'removed' | 'repriced' | 'unchanged';

export interface PackageChange {
  packageId: string;
  /** The catalog's name, when it has one. Absent means "fall back to the id". */
  name?: string;
  kind: PackageChangeKind;
  /** Summed line nets BEFORE, integer cents. Every reduction is already inside them. */
  beforeCents: number;
  afterCents: number;
  deltaCents: number;
  beforeLocationIds: string[];
  afterLocationIds: string[];
  beforeDiscount?: PlanDiscount;
  afterDiscount?: PlanDiscount;
  /**
   * The same money, at a DIFFERENT set of locations — a bills-once package that
   * was moved. `kind` is `unchanged` (the customer's total is identical) but the
   * plan is not, and a move that reads as "nothing happened" is how a placement
   * repair gets applied twice.
   */
  movedLocations: boolean;
}

export interface LocationChange {
  locationId: string;
  beforeCents: number;
  afterCents: number;
  deltaCents: number;
  /** This store bills nothing, on a plan that bills something. See `location_bills_nothing`. */
  billsNothing: boolean;
}

export interface PlanChangeSummary {
  /** FALSE means there was no active assignment at all — not that it billed zero. */
  hadPrior: boolean;
  beforeTotalCents: number;
  afterTotalCents: number;
  deltaCents: number;
  changes: PackageChange[];
  locationChanges: LocationChange[];
  hasChange: boolean;
}

export interface PlanComposition {
  assignment: PlanAssignmentRow;
  locationIds: string[];
  perLocation: ComposedLocation[];
  /**
   * ⚠️ The overall discount, echoed for DISPLAY AND AUDIT ONLY — it is ALREADY
   * APPLIED. Since §13/B1 there is no subscription-level Stripe discount for it
   * to ride on, so the server apportions it pro-rata across the lines and folds
   * it into every `netCents` and into `totals`. Subtracting it from
   * `totals.netCents` shows a customer a DOUBLE-discounted price. Read
   * `payableMonthlyCents()`, never arithmetic of your own.
   */
  subscriptionDiscount?: PlanDiscount;
  /** The server saying, machine-readably, that `subscriptionDiscount` is already in `totals`. */
  subscriptionDiscountApplied?: boolean;
  totals: {
    listCents: number;
    /** `listCents - netCents` — per-package AND overall reductions together. */
    discountCents: number;
    /** The TRUE payable. Nothing remains to be applied to it. */
    netCents: number;
    overallDiscountCents?: number;
  };
  errors: ComposeError[];
  warnings: ComposeWarning[];
  /** packageId -> the catalog's name. Display only; it prices nothing. */
  packageNames?: Record<string, string>;
  /**
   * The plan IN FORCE, composed server-side from the same catalog read as the
   * plan above. Present only when `{ compareToActive: true }` was asked for.
   * `null` means there genuinely is no active assignment; a `beforeError`
   * beside a null `before` means there IS one and it could not be priced —
   * which is "unknown", never "zero".
   */
  before?: { assignmentId?: string; perLocation: ComposedLocation[]; totals: PlanComposition['totals'] } | null;
  beforeError?: string;
  changeSummary?: PlanChangeSummary;
}

/** How the sync went and who was told — the server's own account of the publish. */
export interface PublishOutcome {
  sync?: SyncSubscriptionResult;
  /** FALSE when the sync could not be STARTED — a different fact from "it failed". */
  syncRan: boolean;
  syncError?: string;
  emails: {
    tenant: boolean;
    admin: boolean;
    /** Why the customer was not emailed. Present whenever `tenant` is false. */
    withheldReason?: string;
  };
}

export interface PlanActivationResult extends PlanComposition {
  supersededAssignmentIds: string[];
  /** locationId -> name, resolved server-side. */
  locationNames?: Record<string, string>;
  /**
   * What the activation set in motion: the Stripe sync and the two emails.
   *
   * OPTIONAL so a response from a billing-service that predates it still
   * parses — but `publishReport()` treats a missing one as "we do not know
   * whether the money moved", never as success.
   */
  publish?: PublishOutcome;
}

export interface PlanAssignmentListResult {
  items: PlanAssignmentRow[];
  count: number;
  /** A stored row that no longer parses — reported, never dropped. */
  invalid: { assignmentId: string; reason: string }[];
  limit?: number;
  /**
   * Present does NOT promise more rows — DynamoDB returns a key whenever the
   * page limit is reached, including on the last page. A pager must tolerate
   * one final empty page.
   */
  nextCursor?: string;
  status?: 'active';
}

/**
 * The composed monthly amount the tenant will actually be charged.
 *
 * It is `totals.netCents` and NOTHING ELSE. The overall discount is already
 * apportioned into the line nets, so subtracting `subscriptionDiscount` here
 * would discount the customer twice. This function exists so there is exactly
 * one place in the UI that answers "what is the number", and it is a lookup,
 * never a calculation.
 */
export const payableMonthlyCents = (c: Pick<PlanComposition, 'totals'>): number => c.totals.netCents;

/**
 * TRUE when an overall discount is present but the server did NOT confirm it is
 * already applied — neither via `subscriptionDiscountApplied` nor via
 * `totals.overallDiscountCents`.
 *
 * That combination cannot come from the deployed billing-service, which always
 * sends both. If it ever did, the UI could not know whether `totals.netCents`
 * includes the overall discount or not, and BOTH readings are wrong in a
 * different direction (overstating the bill, or double-discounting it). The
 * builder must then refuse to show a total and refuse to activate, rather than
 * pick one and be silently wrong about money.
 */
export const overallDiscountUnverified = (c: PlanComposition): boolean =>
  !!c.subscriptionDiscount
  && c.subscriptionDiscountApplied !== true
  && c.totals.overallDiscountCents === undefined;

/** `20% off` / `$15.00 off` / `—`. Display only; the arithmetic is the server's. */
export function describeDiscount(d?: PlanDiscount): string {
  if (!d) return '—';
  if (d.percentOff !== undefined) return `${d.percentOff}% off`;
  if (d.amountOffCents !== undefined) return `${money(d.amountOffCents)} off`;
  return '—';
}

/**
 * The key an assignment names a catalog SKU by, matching the server's own
 * resolver: a `package` SKU is indexed under its `packageId`, and every other
 * software SKU (a `plan`, or a package with a blank `packageId`) under its
 * `skuId`. Getting this wrong is not a cosmetic bug — the package would resolve
 * to no SKU and the plan could never be activated.
 */
export const packageKeyOf = (
  sku: Pick<BillingSkuRow, 'skuId' | 'type'> & { packageId?: string },
): string => (sku.type === 'package' && sku.packageId ? sku.packageId : sku.skuId);

/** How a discount is being expressed in the builder. One mode — never two. */
export type DiscountMode = 'none' | 'percent' | 'amount';

export interface DiscountInput {
  mode: DiscountMode;
  /** Percent (0–100) or DOLLARS, as typed. Blank means "no discount yet". */
  value: string;
  reason?: string;
}

/**
 * What is wrong with this discount, in the operator's words — or null.
 *
 * Mirrors `DiscountSchema` so a quote that the server would refuse never reaches
 * it looking like a valid price. A blank value is NOT an error: it means the
 * operator has not typed a number yet, and the line simply bills at list.
 */
export function discountInputError(input?: DiscountInput): string | null {
  if (!input || input.mode === 'none') return null;
  const raw = (input.value ?? '').trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 'Enter a number.';
  if (n < 0) return 'A discount can never be negative.';
  if (input.mode === 'percent' && n > 100) return 'A percentage discount must be between 0 and 100.';
  return null;
}

/**
 * One mode in, at most one field out.
 *
 * The percent-or-amount contract is enforced STRUCTURALLY rather than by
 * validation: the input carries a single `mode`, so a body with both
 * `percentOff` and `amountOffCents` is not something this function can produce.
 * Returns undefined for 'none', for a blank value, and for a value the server
 * would refuse — an invalid number must never be silently rounded into a
 * discount the operator did not type.
 */
export function toDiscount(input?: DiscountInput): PlanDiscount | undefined {
  if (!input || input.mode === 'none') return undefined;
  if (discountInputError(input)) return undefined;
  const raw = (input.value ?? '').trim();
  if (!raw) return undefined;
  const n = Number(raw);
  const reason = (input.reason ?? '').trim();
  const d: PlanDiscount = input.mode === 'percent'
    ? { percentOff: n }
    : { amountOffCents: Math.round(n * 100) };
  return reason ? { ...d, reason } : d;
}

/**
 * A discount editor's empty state. Spread it (`{ ...BLANK_DISCOUNT }`) rather
 * than storing the object itself, so two rows can never share one reference.
 */
export const BLANK_DISCOUNT: DiscountInput = { mode: 'none', value: '', reason: '' };

/**
 * A STORED discount, back into the single-mode shape an editor holds — the
 * exact inverse of `toDiscount`.
 *
 * Shared rather than copied because both editors of per-package discounts (the
 * tenant's plan, and a deal's package composition) have to agree about what
 * `{ amountOffCents: 2500 }` looks like in a form. Two copies of this that
 * rounded differently would show the same stored discount as two different
 * numbers on two screens.
 */
export function discountToInput(d?: PlanDiscount): DiscountInput {
  if (!d) return { ...BLANK_DISCOUNT };
  if (d.percentOff !== undefined) return { mode: 'percent', value: String(d.percentOff), reason: d.reason || '' };
  if (d.amountOffCents !== undefined) {
    return { mode: 'amount', value: centsToDollarString(d.amountOffCents), reason: d.reason || '' };
  }
  return { ...BLANK_DISCOUNT };
}

/**
 * Integer cents -> a plain `149.50` for a number input. Integer arithmetic
 * only: `cents / 100` is a float, and a float is never allowed to be the thing
 * a money value is reconstructed from.
 *
 * Unlike `money()` this emits NO currency symbol and NO thousands separator —
 * an `<input type="number">` rejects both.
 */
export function centsToDollarString(cents: number): string {
  const safe = Math.trunc(Number.isFinite(cents) ? cents : 0);
  const sign = safe < 0 ? '-' : '';
  const abs = Math.abs(safe);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * A typed dollar amount -> INTEGER CENTS, or the reason it is not a price.
 *
 * Parses the decimal string DIGIT BY DIGIT rather than going through
 * `Number(x) * 100`, whose product is frequently NOT an integer at all —
 * `Number('0.29') * 100` is `28.999999999999996`. A `Math.round` on top of that
 * does give the right answer, but only because it is there; this parser never
 * produces a non-integer in the first place, so nothing downstream has to
 * correct one. Money is integer cents from the first operation, not the last.
 */
export function dollarsToCents(raw: string): { cents?: number; error?: string } {
  const s = (raw ?? '').trim().replace(/^\$/, '').replace(/,/g, '');
  if (!s) return { error: 'Enter an amount.' };
  if (!/^\d+(\.\d{1,2})?$/.test(s)) {
    return { error: 'Enter a positive whole-cent amount, like 149 or 149.50.' };
  }
  const [whole, frac = ''] = s.split('.');
  const cents = Number(whole) * 100 + Number(`${frac}00`.slice(0, 2));
  if (!Number.isSafeInteger(cents)) return { error: 'That amount is too large.' };
  return { cents };
}

export interface PlanDraftInput {
  planPackageIds: string[];
  perPackage: Record<string, DiscountInput>;
  overall?: DiscountInput;
  /** null/undefined = no explicit placement (the scope rule decides). */
  locationPackageIds?: Record<string, string[]> | null;
  /** Set to REPLACE an existing draft in place; omit to mint a new one. */
  assignmentId?: string | null;
  /**
   * A free period granted by THIS agreement, in whole days (§12/A4).
   *
   * OMITTED FROM THE BODY when this is `undefined`, which is what every caller
   * that predates the field passes — so their request is byte-for-byte what it
   * always was, and a draft replace leaves whatever trial the stored row
   * carries alone. Pass a NUMBER (including `0`) to state the trial
   * explicitly; `0` is how a replace clears one.
   */
  trialDays?: number;
}

export interface PlanDraftBody {
  planPackageIds: string[];
  discounts: { perPackage: Record<string, PlanDiscount>; overall?: PlanDiscount };
  locationPackageIds?: Record<string, string[]>;
  assignmentId?: string;
  /** Present only when the caller stated one. See `PlanDraftInput.trialDays`. */
  trialDays?: number;
}

/**
 * The request body for both `POST /plan-assignments/{tenantId}` and its
 * `/preview` — deliberately ONE builder, so the plan that is previewed is
 * byte-for-byte the plan that is saved and then activated.
 *
 * Three contracts live here:
 *
 *  1. **`discounts` is ALWAYS sent, and its `perPackage` is TRIMMED to the
 *     packages still in `planPackageIds`.** A draft replace MERGES the body
 *     over the stored row, so omitting `discounts` would resurrect the stored
 *     one — and a discount naming a package that is no longer sold is refused
 *     with a 400 ("the discount would never apply"). Removing a discounted
 *     package from a draft therefore only works because the trimmed object goes
 *     with the trimmed id list.
 *  2. **`locationPackageIds` is trimmed the same way.** The schema refuses a
 *     placement naming a package that is not in `planPackageIds`.
 *  3. **Keys are sorted and ids deduped**, so the body is a stable function of
 *     the plan. The builder hashes it to know whether the totals on screen were
 *     computed from the plan currently on screen.
 */
export function buildPlanDraftBody(input: PlanDraftInput): PlanDraftBody {
  const planPackageIds = Array.from(new Set(input.planPackageIds.filter(Boolean)));
  const selected = new Set(planPackageIds);

  const perPackage: Record<string, PlanDiscount> = {};
  for (const key of Object.keys(input.perPackage || {}).sort()) {
    if (!selected.has(key)) continue;             // contract 1 — the trim
    const discount = toDiscount(input.perPackage[key]);
    if (discount) perPackage[key] = discount;
  }

  const overall = toDiscount(input.overall);
  const body: PlanDraftBody = {
    planPackageIds,
    discounts: overall ? { perPackage, overall } : { perPackage },
  };

  if (input.locationPackageIds) {
    const placement: Record<string, string[]> = {};
    for (const locationId of Object.keys(input.locationPackageIds).sort()) {
      placement[locationId] = Array.from(
        new Set((input.locationPackageIds[locationId] || []).filter((id) => selected.has(id))),
      );                                          // contract 2 — the same trim
    }
    body.locationPackageIds = placement;
  }

  /*
    A trial is never sent. The field survives on `PlanDraftInput` so existing
    callers and stored drafts still typecheck, but stating it would ask the
    server for something it no longer applies: completion creates every
    subscription billing from the first cycle.
  */

  if (input.assignmentId) body.assignmentId = input.assignmentId;
  return body;
}

// ---------------------------------------------------------------------------
// Did the money actually move?
// ---------------------------------------------------------------------------

export interface PublishReport {
  /** TRUE only when billing-service confirmed the subscriptions now match this plan. */
  moneyMoved: boolean;
  /** Why it did not, in the server's own words — or null when it did. */
  problem: string | null;
  customerNotified: boolean;
  /**
   * TRUE only when our own admin copy reached the queue.
   *
   * Reported separately and never assumed: with SQS down `publishActivation`
   * returns `{ tenant: false, admin: false }`, and a screen that says "our
   * admins were emailed" on the strength of the customer copy being withheld
   * would be describing a notification nobody got. The same is true when
   * ADMIN_EMAIL is not configured.
   */
  adminNotified: boolean;
  /** Why the customer was not told. Present whenever `customerNotified` is false. */
  withheldReason?: string;
  /**
   * TRUE when we do not know what happened at all — the publish request never
   * came back with an answer. See `isIndeterminateActivation`: the assignment
   * may be active, half-synced, or untouched, and the ONLY honest thing to do
   * is say so and reload.
   */
  indeterminate?: boolean;
}

/**
 * What to TELL THE OPERATOR after a publish.
 *
 * A 200 from `activate` means the assignment was RECORDED. It does not mean the
 * customer's invoice moved — the Stripe sync runs after the transaction and can
 * fail on its own — so reporting "price changed" on the 200 alone tells an
 * operator something that may not be true yet. Equally, reporting failure
 * because a field is missing would tell them their change did not land when it
 * did.
 *
 * So: success is CLAIMED, never assumed. A response with no `publish` at all
 * (an older billing-service) is "we cannot confirm", which reads as a warning
 * rather than either lie.
 */
export function publishReport(result: Pick<PlanActivationResult, 'publish'>): PublishReport {
  const publish = result.publish;
  if (!publish) {
    return {
      moneyMoved: false,
      problem: 'billing-service did not report whether the subscription was updated — check the tenant\'s billing sync before telling the customer anything',
      customerNotified: false,
      adminNotified: false,
      withheldReason: 'this billing-service did not report whether the customer was emailed',
    };
  }
  const sync = publish.sync;
  const problem = !publish.syncRan
    ? (publish.syncError
      ? `the subscription sync could not be started: ${publish.syncError}`
      : 'the subscription sync could not be started')
    : !sync
      ? 'the subscription sync returned no result'
      : sync.skipped
        ? `the subscription sync reported "${sync.skipped}" straight after an activation, so Stripe was not updated`
        : sync.errors.length
          ? sync.errors[0].reason
          : !sync.synced
            ? 'the subscription sync did not confirm the subscriptions now match this plan'
            : null;
  return {
    moneyMoved: problem === null,
    problem,
    customerNotified: publish.emails.tenant === true,
    adminNotified: publish.emails.admin === true,
    ...(publish.emails.tenant ? {} : { withheldReason: publish.emails.withheldReason }),
  };
}

/**
 * The publish request never came back with an answer.
 *
 * An API Gateway timeout is the case that matters: `activate` now reads and
 * writes one Stripe subscription per location, and if it runs past the 29s
 * ceiling the gateway kills the RESPONSE — the Lambda carries on and may well
 * finish. The browser sees a 504 (or a bare network failure) and knows nothing:
 * the assignment may be active, the sync half-applied, and the emails sent.
 *
 * So a 5xx is treated as INDETERMINATE rather than as a failure. It is the safe
 * direction in both readings: if nothing happened, "reload before retrying" is
 * merely cautious; if something did, it is the only advice that does not lead
 * an operator to retry a change that already landed.
 *
 * A 4xx is NOT indeterminate — the server answered, and its answer ("already
 * active", "not found", a validation refusal) is the truth about what happened.
 */
export interface ActivationTransportError extends Error {
  /** The HTTP status, or 0 when the request never got a response at all. */
  status: number;
  indeterminate: boolean;
}

export const isIndeterminateActivation = (e: unknown): e is ActivationTransportError =>
  !!e && typeof e === 'object' && (e as any).indeterminate === true;

/**
 * What to show an operator whose publish request never came back.
 *
 * NOTE ON THE ADVICE. An earlier version of this told the operator that
 * "retrying a change that already landed is refused, not repeated". That is
 * true only of retrying the SAME `assignmentId` — the 409 in `activate` — and
 * it is not the path they are on: the indeterminate handler reloads, which
 * clears the draft id, so the next press mints a NEW draft that activates
 * cleanly and supersedes the identical plan. Promising a protection that does
 * not apply to the road in front of them is the same defect as a comment naming
 * a safety net that is not there. The real protection is `publishNoOp`, which
 * refuses the republish outright; the advice now describes THAT.
 */
export const indeterminatePublishReport = (message: string): PublishReport => ({
  moneyMoved: false,
  problem: `${message} The plan may or may not have been published, and the subscription may be half-updated — so reload and check what is in force before doing anything else. If it did land, publishing the same plan again is refused rather than repeated.`,
  customerNotified: false,
  adminNotified: false,
  indeterminate: true,
});

// ---------------------------------------------------------------------------
// What to SAY about who was told
// ---------------------------------------------------------------------------

/**
 * Whether the CUSTOMER was emailed — as a sentence that never claims what
 * nobody knows.
 *
 * Under `indeterminate` the publish may have completed and emailed them, which
 * is the entire meaning of the word; rendering `withheldReason` through an
 * assertive template produced "The customer has not been emailed: we do not
 * know whether the customer was emailed", whose first clause states a fact
 * nobody has. So `indeterminate` is answered FIRST, and neither direction is
 * asserted.
 */
export function customerNotifiedSentence(report: PublishReport): string {
  if (report.indeterminate) {
    return 'We cannot tell whether the customer was emailed — check before sending them anything else.';
  }
  if (report.customerNotified) return 'The customer has been emailed exactly what changed.';
  const why = (report.withheldReason || '').replace(/^the customer was NOT emailed because /, '');
  return why
    ? `The customer has not been emailed: ${why}.`
    : 'The customer has not been emailed.';
}

/** The same discipline for OUR OWN copy — see `PublishReport.adminNotified`. */
export function adminNotifiedSentence(report: PublishReport): string {
  if (report.adminNotified) return 'Our admin copy was queued.';
  if (report.indeterminate) return 'We cannot tell whether our own admin copy was sent.';
  return 'Our admin copy was NOT sent — nobody has been notified but you.';
}

// ---------------------------------------------------------------------------
// REFUSING A PUBLISH THAT CHANGES NOTHING
// ---------------------------------------------------------------------------

/**
 * The comparable IDENTITY of a plan: what it sells, at what discount, placed
 * where — as one stable string.
 *
 * Keys and ids are sorted and a discount is reduced to the one field it may
 * carry, so two records that say the same thing in a different order produce
 * the same string. It deliberately ignores everything that is NOT the deal:
 * `assignmentId`, `createdBy`, timestamps, `status`, `supersedesAssignmentId`.
 *
 * `trialDays` is deliberately ignored too. A publish never re-sends a trial
 * (Task 11), so a difference in that field alone changes nothing about what the
 * customer is charged and must not be what makes a no-op look like a change.
 */
export function planIdentity(source: {
  planPackageIds?: string[];
  discounts?: { perPackage?: Record<string, PlanDiscount>; overall?: PlanDiscount };
  locationPackageIds?: Record<string, string[]>;
}): string {
  const discount = (d?: PlanDiscount) => (!d ? null : {
    percentOff: d.percentOff ?? null,
    amountOffCents: d.amountOffCents ?? null,
    reason: (d.reason || '').trim(),
  });
  const perPackage: Record<string, unknown> = {};
  const stored = source.discounts?.perPackage || {};
  for (const key of Object.keys(stored).sort()) {
    const d = discount(stored[key]);
    if (d) perPackage[key] = d;
  }
  const placement: Record<string, string[]> | null = source.locationPackageIds
    ? Object.fromEntries(
      Object.keys(source.locationPackageIds).sort()
        .map((locationId) => [locationId, Array.from(new Set(source.locationPackageIds![locationId] || [])).sort()]),
    )
    : null;
  return JSON.stringify({
    planPackageIds: Array.from(new Set(source.planPackageIds || [])).sort(),
    perPackage,
    overall: discount(source.discounts?.overall),
    placement,
  });
}

export type PublishNoOp =
  | { kind: 'identical'; reason: string }
  | { kind: 'same-money'; reason: string }
  | null;

/**
 * Would publishing this change anything?
 *
 * A publish that changes NOTHING has no legitimate use and three real costs: it
 * supersedes a commercial record for no reason, it churns the subscription, and
 * it fires the plan-change notifications. It became reachable the moment the
 * indeterminate path started reloading: the reload clears the draft id, so the
 * obvious "try again" mints a NEW draft over a plan that may already be live.
 *
 * Two distinct answers, because they are two different situations:
 *
 *  - **`identical`** — the composed money AND the record match what is in
 *    force. Nothing whatsoever changes. REFUSED.
 *  - **`same-money`** — the composed money is unchanged but the record is not:
 *    a discount re-expressed from 10% to $10 at the same net, a reason
 *    corrected. That is a legitimate repair of how a price was arrived at, so
 *    it is ALLOWED — with the warning that the customer will not be emailed,
 *    because nothing they are billed for moved.
 *
 * Both halves come from the SERVER. `changeSummary.hasChange` is
 * billing-service's own comparison of the two compositions it priced from one
 * catalog read; `planIdentity` compares the assignment the server echoed
 * against the assignment row the server said is active. Nothing here
 * reconstructs a plan or a price.
 */
export function publishNoOp(args: {
  /** The preview of the plan about to be published, with `compareToActive`. */
  preview: Pick<PlanComposition, 'assignment' | 'changeSummary' | 'before' | 'beforeError'>;
  /** The assignment billing-service reports as active, or null when there is none. */
  activeAssignment: PlanAssignmentRow | null;
}): PublishNoOp {
  const summary = args.preview.changeSummary;
  // No comparison in hand: the before → after gate already refuses to publish
  // in that state, and guessing here would either block a real change or wave
  // through a no-op.
  if (!summary) return null;
  // A first activation is never a no-op — it moves the tenant out of the
  // no-plan-assignment population that every sync path skips (D12).
  if (!summary.hadPrior || !args.activeAssignment) return null;
  // The "before" could not be priced, so `hasChange` was computed against
  // nothing and means nothing. The gate blocks this state anyway.
  if (args.preview.beforeError) return null;
  // Comparing against a DIFFERENT assignment than the one composed would answer
  // a question nobody asked; the builder blocks that case separately.
  if (args.preview.before?.assignmentId
    && args.preview.before.assignmentId !== args.activeAssignment.assignmentId) return null;
  if (summary.hasChange) return null;

  const same = planIdentity(args.preview.assignment) === planIdentity(args.activeAssignment);
  return same
    ? {
      kind: 'identical',
      reason: 'This is exactly the plan already in force — same packages, same discounts, same stores, same monthly. Publishing it would supersede the current record, touch the subscription and email the tenant, all to change nothing.',
    }
    : {
      kind: 'same-money',
      reason: 'The monthly amounts are unchanged; only how the discount is recorded differs. Publishing this updates the record of how the price was arrived at — the tenant is not emailed, because nothing they are billed for moved.',
    };
}

// ---------------------------------------------------------------------------
// ONE STORE'S SHARE OF A TENANT-WIDE CHANGE
// ---------------------------------------------------------------------------

export interface LocationPackageChange {
  packageId: string;
  name?: string;
  /** What happened AT THIS LOCATION — not what happened to the tenant. */
  kind: PackageChangeKind;
  /** This location's own line net before, in cents. 0 when it billed nothing here. */
  beforeCents: number;
  afterCents: number;
  deltaCents: number;
  /** TRUE when this package also bills somewhere else, before or after. */
  billsElsewhere: boolean;
  /** The tenant-wide row this came from, so the whole-agreement figures stay reachable. */
  tenant: PackageChange;
}

/**
 * Re-cut a tenant-wide change summary as ONE LOCATION'S change.
 *
 * This exists because of a mislabelling that would have put a number in front
 * of an operator's consent that was not the number they thought. The
 * change-price editor is headed with one store's name, but
 * `PlanChangeSummary.changes` sums each package ACROSS EVERY LOCATION — so on a
 * three-store tenant a dialog titled "Publish this price for Downtown?" showed
 * "$40.00/mo → $32.00/mo" when Downtown's share was $20.00 → $16.00. The
 * acknowledgement is the moment of consent; the figures beside it have to be the
 * ones being consented to.
 *
 * NO ARITHMETIC HAPPENS HERE — and there is none available to get wrong.
 * `composeSubscription` emits at most ONE line per (package, location), so a
 * store's figure for a package is a LOOKUP of a server-composed `netCents`, not
 * a sum and certainly not a share of a total divided up in the browser. Both
 * sides come from the server: `before` is `preview.before.perLocation` (the
 * assignment actually in force, composed server-side) and `after` is
 * `preview.perLocation`.
 *
 * Every tenant-wide row is kept, including packages that do not bill at this
 * store at all — because the editor CAN change those (removing a package
 * removes it everywhere), and a per-store view that hid a plan-wide removal
 * would trade one mislabelling for a worse omission. Such a row reads
 * `kind: 'unchanged'` with both figures at 0, and `tenant` carries what really
 * moved.
 */
export function locationChangeRows(args: {
  locationId: string;
  /** `preview.before?.perLocation` — null when there is no prior assignment. */
  before: ComposedLocation[] | null | undefined;
  /** `preview.perLocation`. */
  after: ComposedLocation[];
  /** `changeSummary.changes` — the tenant-wide rows, in the server's order. */
  changes: PackageChange[];
}): LocationPackageChange[] {
  const here = (locations: ComposedLocation[] | null | undefined): Map<string, number> => {
    const found = (locations || []).find((l) => l.locationId === args.locationId);
    return new Map((found?.items || []).map((item) => [item.packageId, item.netCents]));
  };
  const beforeHere = here(args.before);
  const afterHere = here(args.after);

  return args.changes.map((tenant) => {
    const hadHere = beforeHere.has(tenant.packageId);
    const hasHere = afterHere.has(tenant.packageId);
    const beforeCents = beforeHere.get(tenant.packageId) ?? 0;
    const afterCents = afterHere.get(tenant.packageId) ?? 0;
    const kind: PackageChangeKind = !hadHere && !hasHere
      ? 'unchanged'
      : !hadHere
        ? 'added'
        : !hasHere
          ? 'removed'
          : beforeCents !== afterCents
            ? 'repriced'
            : 'unchanged';
    return {
      packageId: tenant.packageId,
      ...(tenant.name ? { name: tenant.name } : {}),
      kind,
      beforeCents,
      afterCents,
      deltaCents: afterCents - beforeCents,
      billsElsewhere: tenant.beforeLocationIds.some((id) => id !== args.locationId)
        || tenant.afterLocationIds.some((id) => id !== args.locationId),
      tenant,
    };
  });
}

// ---------------------------------------------------------------------------
// "Adjust the final subscription price"
// ---------------------------------------------------------------------------

/**
 * The sum of the line nets BEFORE the overall discount is folded in.
 *
 * Both halves are the server's own numbers: `totals.netCents` already has the
 * overall discount inside it, and `totals.overallDiscountCents` is exactly how
 * much of it that is (the server reads that back OUT of the apportioned shares,
 * so the two agree to the cent by construction). Adding them back is therefore
 * a reversal of one documented subtraction, not a re-derivation of anything.
 */
export const preOverallCents = (c: Pick<PlanComposition, 'totals'>): number =>
  c.totals.netCents + (c.totals.overallDiscountCents ?? 0);

export interface TargetPriceResult {
  /** The overall discount to send, ready for `buildPlanDraftBody`. */
  discount?: DiscountInput;
  /** Why the target cannot be expressed, in the operator's words. */
  error?: string;
}

/**
 * Work out the OVERALL DISCOUNT that lands the composed total on an exact figure.
 *
 * The operator's question is "make this £X a month", and the model's only
 * tenant-wide lever is a discount — so this converts the one into the other.
 * It is deliberately the ONLY place in the UI that derives a discount, and it
 * is safe to be, because of what it does and does not do:
 *
 *  - it produces an INPUT to the server, never a price to display. The figure
 *    on screen still comes back from `POST …/preview`, so if this were ever
 *    wrong the operator would see the wrong number before publishing rather
 *    than after;
 *  - the arithmetic is exact, not approximate. `applyDiscount` on a flat amount
 *    is `round(list - amountOff)` over integers, and `apportionCents`
 *    guarantees the shares sum to the discount exactly — so the composed total
 *    lands on the target to the cent, every time.
 *
 * A target ABOVE the pre-discount total is refused rather than silently clamped:
 * a discount cannot raise a price, and an operator who typed a bigger number
 * needs to hear that, not watch nothing happen. `reason` is carried through so
 * the record still says why the money was given away.
 */
export function overallDiscountForTarget(
  composition: Pick<PlanComposition, 'totals'>,
  targetCents: number,
  reason?: string,
): TargetPriceResult {
  if (!Number.isFinite(targetCents) || targetCents < 0) {
    return { error: 'Enter a monthly amount of 0 or more.' };
  }
  const target = Math.round(targetCents);
  const preOverall = preOverallCents(composition);
  if (target > preOverall) {
    return {
      error: `A discount cannot raise a price. The most this plan can bill is $${(preOverall / 100).toFixed(2)}/mo — reduce a per-package discount, or add a package, to go higher.`,
    };
  }
  const amountOffCents = preOverall - target;
  if (amountOffCents === 0) return { discount: { mode: 'none', value: '', reason: reason || '' } };
  return {
    discount: { mode: 'amount', value: (amountOffCents / 100).toFixed(2), reason: reason || '' },
  };
}

// ---------------------------------------------------------------------------
// PLACEMENT — which store bills which package (spec §14/C2)
// ---------------------------------------------------------------------------

/**
 * Turn the IMPLICIT scope rule into an EXPLICIT placement that composes to
 * exactly the same money.
 *
 * An assignment with no `locationPackageIds` is billed by scope: a
 * `location`-scoped package on every location, a `tenant`-scoped one on the
 * FIRST. To enable or disable a package at ONE store, that implicit rule has to
 * become an explicit list first — and the conversion must not move a single
 * cent, or an operator toggling one package would silently reprice the others.
 *
 * **Every package at every location is exactly equivalent to the scope rule**,
 * and that is why this function needs to know no scopes at all:
 *
 *  - a `location`-scoped package placed everywhere bills everywhere — the same
 *    as the scope rule;
 *  - a `tenant`-scoped package placed everywhere still bills ONCE, because
 *    `composeSubscription` takes `placed.slice(0, 1)` for tenant scope — the
 *    same first location the scope rule picks, since both read the same
 *    server-ordered `locationIds`.
 *
 * So the conversion is provably a no-op on the composed total, with no scope
 * lookup to get wrong. `locationIds` MUST be the list the server returned, in
 * the server's order, because "the first location" is defined by that order.
 */
export function materialisePlacement(
  packageIds: string[],
  locationIds: string[],
): Record<string, string[]> {
  const all = Array.from(new Set(packageIds.filter(Boolean)));
  const placement: Record<string, string[]> = {};
  for (const locationId of locationIds) placement[locationId] = [...all];
  return placement;
}

export interface PlacementToggle {
  locationId: string;
  packageId: string;
  /** TRUE to bill this package at this location, FALSE to stop. */
  enabled: boolean;
  /**
   * A `tenant`-scoped package BILLS ONCE (D5) and enabling it here therefore
   * MOVES it rather than adding a second charge. Getting this wrong is the one
   * way this function could double-bill, so it is required rather than inferred.
   */
  scope: 'tenant' | 'location';
}

/**
 * Enable or disable one package at ONE location.
 *
 * The `tenant` branch is the whole reason this is a function rather than three
 * lines inline. `composeSubscription` bills a tenant-scoped package at the
 * FIRST location it is placed at, so merely ADDING a second location would
 * leave it billing exactly where it already did — a toggle that visibly does
 * nothing. Enabling one therefore REPLACES the placement with this location
 * alone, which is what "billed once, here" means, and it can never raise the
 * number of locations billing it above one.
 *
 * Disabling only ever REMOVES. A tenant-scoped package disabled at the store it
 * was billing falls back to whatever other location still lists it, or to
 * nowhere — which the server calls out as `placed_nowhere` rather than letting
 * it vanish quietly.
 */
export function togglePlacement(
  placement: Record<string, string[]>,
  toggle: PlacementToggle,
): Record<string, string[]> {
  const next: Record<string, string[]> = {};
  for (const locationId of Object.keys(placement)) {
    const current = placement[locationId] || [];
    const isTarget = locationId === toggle.locationId;
    let ids: string[];
    if (toggle.enabled) {
      // A bills-once package moves; a per-store one is simply added here.
      ids = isTarget
        ? Array.from(new Set([...current, toggle.packageId]))
        : (toggle.scope === 'tenant' ? current.filter((id) => id !== toggle.packageId) : current);
    } else {
      ids = isTarget ? current.filter((id) => id !== toggle.packageId) : current;
    }
    next[locationId] = ids;
  }
  // A location absent from the stored placement bills nothing on this plan —
  // which is precisely the leak §14/C2 is about. Enabling a package there has
  // to create the entry, or the toggle would be silently ignored.
  if (toggle.enabled && !Object.prototype.hasOwnProperty.call(next, toggle.locationId)) {
    next[toggle.locationId] = [toggle.packageId];
  }
  return next;
}

/** Thrown when `activate` refuses because the composition has errors (400). */
export interface PlanActivationRefusal extends Error {
  refusal: { assignmentId?: string; errors: ComposeError[]; warnings: ComposeWarning[] };
}

export const isPlanActivationRefusal = (e: unknown): e is PlanActivationRefusal =>
  !!e && typeof e === 'object' && Array.isArray((e as any)?.refusal?.errors);

// ---------------------------------------------------------------------------
// PAYMENT METHODS (spec §14/C3) — how a tenant pays US.
//
// **Card and bank details never touch our servers.** They are collected in the
// browser by Stripe Elements — a CardElement for a card, a SetupIntent plus
// `collectBankAccountForSetup` for ACH — and the only thing this client ever
// sends is an opaque `pm_…` id. Nothing in this section may grow a field
// carrying a PAN, a CVC, an account number or a routing number; that would put
// this platform into PCI scope it is not in today.
//
// A payment method hangs off the tenant's ONE Stripe Customer, so these calls
// are tenant-scoped even though what they change is how EVERY location's
// subscription gets collected.
// ---------------------------------------------------------------------------

/**
 * The whole of what we are willing to know about a stored instrument. The
 * server whitelists these fields out of Stripe's own object — no cardholder
 * name, no billing address, no fingerprint.
 */
export interface StoredPaymentMethod {
  id: string;
  /** 'card' | 'us_bank_account'. Branch on this, never on a zero expiry. */
  type: string;
  /** Card network, or the bank's name for ACH. */
  brand: string;
  /** Stripe's own last four. EMPTY means it could not be read — never render it as real. */
  last4: string;
  /** Card only; 0 for ACH. */
  expMonth: number;
  expYear: number;
  funding: string | null;
  isDefault: boolean;
}

export interface LiveSubscriptionRow {
  subscriptionId: string;
  status: string;
  /** Null when no location could be attributed to it — still a real subscription. */
  locationId: string | null;
  /**
   * WHAT STRIPE WILL ACTUALLY TAKE for this location each month, summed from
   * the subscription's OWN items and normalised to a month.
   *
   * The server has always sent this; the type simply did not declare it, so
   * every caller fell back to a single price on the tenant record instead —
   * `subscriptionCost`, which super-admin stamps from `items.data[0]`. That is
   * one line of thirteen under per-item billing, which is how the order screen
   * came to show "software $39.99" for an account billing $165.91.
   */
  monthlyCents?: number;
  /** How many Stripe items make up that figure. */
  itemCount?: number;
  /** `'addon_order'` for devices/software ordered after signup; absent on a store's own plan. */
  source?: string | null;
  /** On an add-on: the store subscription it sits beside. */
  parentSubscriptionId?: string | null;
  /** On an add-on: the device order it bills. */
  addOnOrderId?: string | null;
  /** On an add-on: its items, named. */
  items?: Array<{ name: string | null; quantity: number; unitAmountCents: number; interval?: string }>;
}

/** One line of what we sent to Stripe — a package or a device, at the amount we billed it. */
export interface SoldLine {
  skuId: string;
  kind?: string;
  quantity: number;
  amountCents: number;
  termMonths?: number;
}

export interface SoldLocation {
  locationId: string;
  name?: string;
  isFree?: boolean;
  softwareNetCents?: number;
  subscriptionId?: string | null;
  invoiceId?: string | null;
  orderId?: string | null;
  /** Every recurring line — packages AND devices. `performCompletion` calls this "OUR record". */
  lines?: SoldLine[];
  /** Deposits and down payments, charged once at checkout. */
  oneTimeLines?: Array<{ skuId: string; quantity: number; amountCents: number }>;
}

export interface OnboardingResultResult {
  tenantId: string;
  completed: boolean;
  status?: string;
  completedAt?: number | null;
  result: { locations?: SoldLocation[]; totals?: Record<string, number> } | null;
}

export interface PaymentMethodsResult {
  tenantId: string;
  /**
   * FALSE means Stripe could not be read. It is NOT the same as an empty list,
   * and the two must never be rendered the same way: "we could not tell you"
   * and "there is nothing on file" lead an operator to opposite actions.
   */
  readable: boolean;
  hasCustomer: boolean;
  paymentMethods: StoredPaymentMethod[];
  defaultPaymentMethodId: string | null;
  liveSubscriptions: LiveSubscriptionRow[];
  /**
   * Every live subscription together — the tenant's whole monthly bill, from
   * Stripe's own items. Absent only when the customer could not be read.
   */
  monthlyTotalCents?: number;
  /** Days between a failed invoice and suspension. Server-sourced so the warning cannot drift. */
  graceDays: number;
  supportedTypes?: string[];
  note?: string;
  error?: string;
}

export interface AttachPaymentMethodResult {
  tenantId: string;
  /** What the server says actually happened — an add, a default change, or a replace. */
  action: 'attach' | 'set_default' | 'replace';
  paymentMethod: StoredPaymentMethod;
  /**
   * What HAPPENED, not what was asked for. `false` with a `defaultError` means
   * the method is on file and is NOT what gets charged.
   */
  madeDefault: boolean;
  /**
   * TRUE for the half-applied case: the CUSTOMER default moved, but at least
   * one subscription that pins its own payment method did not follow.
   * `setDefaultPaymentMethod` is not atomic — it writes the customer default
   * first and repoints each pinning subscription afterwards — so "it threw"
   * does not mean "nothing happened", and this flag is the only thing that
   * separates a partial change from a failed one.
   */
  defaultPartial?: boolean;
  /** Why the default did not fully apply. The server's sentence — render it verbatim. */
  defaultError?: string;
  /** Subscriptions that pinned their own method and WERE moved onto this one. */
  repointedSubscriptionIds: string[];
  /** For a replace: whether the OLD method was detached. */
  replaced: boolean;
  /**
   * Set when the old method was NOT detached. It has two distinct meanings and
   * they must not be collapsed into one sentence:
   *  - with `defaultError`, the old method was deliberately KEPT (either the
   *    new one is not the default at all, or a subscription still pins the old
   *    one) — the tenant may still be charged to the OLD instrument;
   *  - without one, the new method is live and the detach itself failed.
   */
  replaceError?: string;
  affectedLocationIds: string[];
  auditAt: number;
  /** Did an audit row land at all? Absent on a server that predates the field. */
  auditRecorded?: boolean;
  /** A row landed, but a reduced one. */
  auditDegraded?: boolean;
  /** Why the full row failed. */
  auditError?: string;
}

export interface RemovePaymentMethodResult {
  tenantId: string;
  removed: string;
  /**
   * The method promoted to default in its place, or null if there was none to
   * promote — because the removed method was not the default, or because
   * nothing was left. A promotion that FAILED never reaches here: it is a 409
   * (`PaymentMethodRemovalFailure`) and nothing is detached.
   */
  promotedPaymentMethodId: string | null;
  /**
   * TRUE when the promoted method is a bank account Stripe cannot charge yet.
   * It was promoted anyway — the alternative was leaving the tenant with
   * nothing chargeable — and saying nothing about that is exactly what made the
   * original defect a Critical.
   */
  promotedUnverified?: boolean;
  /** Stripe's SetupIntent status for that bank account, e.g. `requires_action`. */
  promotedSetupIntentStatus?: string;
  /** The server's own sentence about the degraded promotion. Render it verbatim. */
  promotionWarning?: string;
  remainingCount: number;
  /** TRUE when this tenant now has live subscriptions and nothing to charge. */
  leftWithNothingToCharge: boolean;
  affectedLocationIds: string[];
  auditAt: number;
  /** Did an audit row land at all? Absent on a server that predates the field. */
  auditRecorded?: boolean;
  /** A row landed, but a reduced one. */
  auditDegraded?: boolean;
  /** Why the full row failed. */
  auditError?: string;
}

/**
 * Thrown when removal is refused because it would leave a tenant with live
 * subscriptions and no way to pay them (409).
 *
 * `warning` is the SERVER'S OWN sentence and must be rendered verbatim. It
 * names the subscription count, the location count and the grace period; a
 * second version composed in the browser would eventually disagree with the
 * rule the server actually applies.
 */
export interface PaymentMethodRemovalRefusal extends Error {
  refusal: {
    warning: string;
    requiresReason: boolean;
    liveSubscriptionCount: number;
    affectedLocationIds: string[];
    graceDays: number;
  };
}

export const isRemovalRefusal = (e: unknown): e is PaymentMethodRemovalRefusal =>
  !!e && typeof e === 'object' && typeof (e as any)?.refusal?.warning === 'string';

/**
 * Thrown when a removal is REFUSED because the replacement could not be made
 * the default first (409). **Nothing was detached** — that is the whole point
 * of promoting before detaching.
 *
 * `customerDefaultMoved` is the field that must not be ignored.
 * `setDefaultPaymentMethod` is not atomic: it writes the customer default and
 * then repoints each pinning subscription, so a failure part-way leaves the
 * tenant's default ALREADY MOVED to another method while the removal itself did
 * not happen. Telling the operator "nothing changed" in that case is the
 * opposite of the truth, and the panel would go on showing the old method as
 * the charged one until it is reloaded.
 */
export interface PaymentMethodRemovalFailure extends Error {
  removalFailure: {
    /** Did `invoice_settings.default_payment_method` already move? */
    customerDefaultMoved: boolean;
    /** The method that IS now the default, when one is. */
    promotedPaymentMethodId: string | null;
    /** The method the server was trying to promote. */
    intendedPromotionPaymentMethodId: string | null;
    /** Subscriptions repointed before the failure. */
    repointedSubscriptionIds: string[];
    /** Stripe's own message. */
    promoteError?: string;
    /**
     * The promotion that DID land gets an audit row of its own, and it can fail
     * like any other. Carried here for the same reason as on a success: the
     * change happened either way.
     */
    auditRecorded?: boolean;
    auditDegraded?: boolean;
    auditError?: string;
  };
}

export const isRemovalFailure = (e: unknown): e is PaymentMethodRemovalFailure =>
  !!e && typeof e === 'object' &&
  typeof (e as any)?.removalFailure?.customerDefaultMoved === 'boolean';

/**
 * Thrown when an attach REFUSES to make an unverified bank account the default
 * (409). Nothing was changed about how the tenant is charged.
 *
 * A bank account collected through microdeposits is attached to the customer
 * immediately but cannot be charged for a day or two. Pointing subscriptions at
 * it would send the tenant into dunning because we "successfully" updated their
 * payment method — so the server refuses.
 *
 * This is parsed as DATA rather than a bare message so the panel can offer the
 * way forward the refusal names: put it on file WITHOUT making it the default.
 * Without that affordance the refusal is a dead end.
 */
export interface BankVerificationRefusal extends Error {
  bankVerification: {
    /** The server's own sentence. Render it verbatim. */
    message: string;
    /** Stripe's SetupIntent status, e.g. `requires_action` for the microdeposit wait. */
    setupIntentStatus: string;
    /** The opaque `pm_…` id that was refused — never an instrument. */
    paymentMethodId: string;
  };
}

export const isBankVerificationRefusal = (e: unknown): e is BankVerificationRefusal =>
  !!e && typeof e === 'object' && typeof (e as any)?.bankVerification?.message === 'string';

/**
 * Card network names, spelled the way platform-app already spells them on the
 * tenant's own billing screen. The same tenant should not read "AMEX" in one
 * place and "American Express" in the other.
 */
const CARD_BRAND_NAMES: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

const formatCardBrand = (brand: string): string =>
  CARD_BRAND_NAMES[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();

/** Bank names arrive from Stripe in caps ("STRIPE TEST BANK"); shouting is not information. */
const titleCase = (s: string): string =>
  s.split(/\s+/).filter(Boolean)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');

/**
 * How one stored instrument reads to a human.
 *
 * Pure, and deliberately refuses to invent anything: a method whose `last4`
 * came back empty is described as unreadable rather than as `•••• ` with a gap
 * where the digits should be. §14/C3 — never render a guessed or blank
 * instrument as if it were real.
 */
export function describePaymentMethod(
  m: Pick<StoredPaymentMethod, 'type' | 'brand' | 'last4' | 'expMonth' | 'expYear' | 'funding'>,
  now: Date = new Date(),
): { label: string; sub: string; isBank: boolean; unreadable: boolean; expired: boolean } {
  const isBank = m.type === 'us_bank_account';
  if (!m.last4) {
    return {
      label: 'Payment method on file (details unreadable)',
      sub: 'Stripe did not return the brand or last four for this method.',
      isBank,
      unreadable: true,
      expired: false,
    };
  }
  const brand = isBank ? titleCase(m.brand || 'bank') : formatCardBrand(m.brand || 'card');
  const label = `${brand} ···· ${m.last4}`;
  if (isBank) {
    return { label, sub: `${m.funding || 'bank'} account · ACH`, isBank, unreadable: false, expired: false };
  }
  // A card expires at the END of its expiry month, so the first dead day is the
  // 1st of the following month. Comparing on the month itself would call a card
  // expired for the whole month it is still perfectly usable in.
  const expired = m.expMonth > 0 && m.expYear > 0 &&
    new Date(m.expYear, m.expMonth, 1).getTime() <= now.getTime();
  const exp = m.expMonth && m.expYear
    ? `${String(m.expMonth).padStart(2, '0')}/${m.expYear}`
    : null;
  return {
    label,
    sub: exp ? `${expired ? 'EXPIRED' : 'expires'} ${exp}` : 'no expiry reported',
    isBank,
    unreadable: false,
    expired,
  };
}

// ---------------------------------------------------------------------------
// What a payment-method call ACTUALLY did
// ---------------------------------------------------------------------------
//
// These live here, not in JSX, for two reasons. The obvious one is that this
// file is testable and a component is not. The load-bearing one is that every
// sentence below is a CLAIM ABOUT MONEY — "this is what gets charged", "the old
// card is gone" — and a claim that is only ever typed into a template is a
// claim nobody can pin. Each branch below has a test that fails if its wording
// starts describing something the response does not say.
//
// Rule for every message here: say only what the response asserts. Where the
// server composed its own sentence — `defaultError`, `replaceError`,
// `promotionWarning` — it is carried through VERBATIM in `detail` rather than
// paraphrased, so the browser and the server can never end up describing the
// same event two different ways.

export type PaymentMethodOutcomeTone = 'success' | 'warn' | 'danger';

export interface PaymentMethodOutcome {
  tone: PaymentMethodOutcomeTone;
  /** One line, short enough for a toast. */
  message: string;
  /** The server's own sentences, verbatim. May be empty. */
  detail: string[];
}

/**
 * How an add / set-default / replace actually landed.
 *
 * The three failure shapes are deliberately NOT collapsed, because an operator
 * has to do something different in each:
 *
 *  - `defaultPartial` — the method IS charged, but a subscription that pins its
 *    own method is still pointing somewhere else. Retry the default change.
 *  - `defaultError` without `defaultPartial` — the method is on file and is NOT
 *    charged. The old instrument is still what collects.
 *  - `replaceError` alone — the new method is charged; the old one is merely
 *    still on file and can be removed with one more click.
 */
export function describeAttachOutcome(res: AttachPaymentMethodResult): PaymentMethodOutcome {
  const detail: string[] = [];
  const repointed = res.repointedSubscriptionIds?.length || 0;

  if (res.defaultPartial) {
    if (res.defaultError) detail.push(res.defaultError);
    detail.push(
      repointed
        ? `${repointed} subscription${repointed === 1 ? ' that pinned its own' : 's that pinned their own'} ` +
          `payment method ${repointed === 1 ? 'was' : 'were'} moved onto it before the failure; at least one was not.`
        : 'No subscription that pins its own payment method was moved onto it.',
    );
    if (res.replaceError) detail.push(res.replaceError);
    return {
      tone: 'warn',
      message:
        'Partly applied: this method IS now the default, but at least one subscription that pins its own ' +
        'payment method could not be moved onto it.',
      detail,
    };
  }

  if (res.defaultError) {
    detail.push(res.defaultError);
    if (res.replaceError) detail.push(res.replaceError);
    return {
      tone: 'danger',
      message: 'The payment method is on file, but it is NOT what gets charged — the default could not be changed.',
      detail,
    };
  }

  if (res.replaceError) {
    detail.push(res.replaceError);
    return {
      tone: 'warn',
      message: res.madeDefault
        ? 'The new payment method is what gets charged. The previous one could not be removed — the list below ' +
          'shows what is actually on file now.'
        : 'The new payment method is on file. The previous one could not be removed — the list below shows ' +
          'what is actually on file now.',
      detail,
    };
  }

  if (res.action === 'replace') {
    return res.replaced
      ? { tone: 'success', message: 'Payment method replaced — the new one is what gets charged and the old one was removed.', detail }
      : { tone: 'warn', message: 'The new payment method is on file, but the server did not confirm the old one was removed.', detail };
  }

  if (res.madeDefault) {
    return {
      tone: 'success',
      message: res.action === 'set_default'
        ? 'Default payment method changed — this is now what gets charged.'
        : 'Payment method added, and it is now what gets charged.',
      detail,
    };
  }

  // Deliberately says nothing about what IS charged: an attach that was not
  // asked to become the default does not report the existing default, and a
  // tenant may have had none to begin with.
  return {
    tone: 'warn',
    message: 'The payment method is on file. It is NOT what gets charged.',
    detail,
  };
}

/**
 * How a removal actually landed.
 *
 * `promotedPaymentMethodId: null` used to be shown as a bare "Payment method
 * removed." — the same sentence a FAILED promotion produced, which is how a
 * tenant could be left unchargeable while the screen said everything was fine.
 * A failed promotion is now a refusal (`isRemovalFailure`) and never reaches
 * here, and the two remaining null cases are told apart by `remainingCount`:
 * a method that was not the default (nothing about billing changed) versus an
 * empty file (nothing left to charge, but no live subscription to fail either).
 */
export function describeRemoveOutcome(res: RemovePaymentMethodResult): PaymentMethodOutcome {
  const detail: string[] = [];
  if (res.promotionWarning) detail.push(res.promotionWarning);

  if (res.leftWithNothingToCharge) {
    return {
      tone: 'danger',
      message: 'Payment method removed. This tenant has live subscriptions and now nothing Stripe can charge.',
      detail,
    };
  }
  if (res.promotedPaymentMethodId && res.promotedUnverified) {
    return {
      tone: 'warn',
      message:
        'Payment method removed. The method promoted in its place is a bank account Stripe cannot charge ' +
        'yet — it is the default anyway, so an invoice falling due before it verifies will fail.',
      detail,
    };
  }
  if (res.promotedPaymentMethodId) {
    return {
      tone: 'success',
      message: 'Payment method removed. The remaining method is now what gets charged.',
      detail,
    };
  }
  if (res.remainingCount > 0) {
    return {
      tone: 'success',
      message: 'Payment method removed. It was not the charged method, so nothing about how this tenant is billed changed.',
      detail,
    };
  }
  return {
    tone: 'warn',
    message:
      'Payment method removed. Nothing is on file now — this tenant has no live subscription for it to fail, ' +
      'but they cannot be billed until a method is added.',
    detail,
  };
}

/**
 * A removal the server REFUSED because the replacement could not be fully
 * promoted. Nothing was detached either way — but whether the customer default
 * moved changes what the operator is now looking at, and the panel has to
 * reload when it did.
 */
export function describeRemovalFailure(
  e: PaymentMethodRemovalFailure,
): PaymentMethodOutcome & { reloadRequired: boolean } {
  const f = e.removalFailure;
  const detail: string[] = [e.message].filter(Boolean);
  if (f.customerDefaultMoved) {
    const repointed = f.repointedSubscriptionIds?.length || 0;
    if (repointed) {
      detail.push(
        `${repointed} subscription${repointed === 1 ? ' that pinned its own' : 's that pinned their own'} ` +
        `payment method ${repointed === 1 ? 'was' : 'were'} moved onto it; at least one was not.`,
      );
    }
    return {
      tone: 'danger',
      message:
        'Nothing was removed — but this tenant’s default HAS already moved to the other method. ' +
        'Retry the removal once the remaining subscriptions can be moved onto it.',
      detail,
      reloadRequired: true,
    };
  }
  return {
    tone: 'danger',
    message:
      'Nothing was removed, and nothing changed: the remaining method could not be made the default first, ' +
      'so removing this one would have left the tenant with nothing Stripe can charge.',
    detail,
    reloadRequired: false,
  };
}

export interface AuditOutcome {
  recorded: 'full' | 'reduced' | 'none' | 'unknown';
  /** Null when there is nothing worth saying — a full row, or a server that did not report. */
  message: string | null;
}

/**
 * What the response says about its OWN audit row.
 *
 * The audit trail is the whole justification for doing this here rather than in
 * the Stripe dashboard, so "it was logged" cannot be an unconditional promise
 * in the footer: the write happens after Stripe has already changed, and it can
 * fail on its own. `unknown` is silent on purpose — an older server that does
 * not report is not evidence either way, and inventing a reassurance would be
 * the same defect as the promise it replaced.
 */
export function describeAuditOutcome(res: {
  auditRecorded?: boolean;
  auditDegraded?: boolean;
  auditError?: string;
}): AuditOutcome {
  const why = res.auditError ? ` (${res.auditError})` : '';
  if (res.auditRecorded === false) {
    return {
      recorded: 'none',
      message:
        `The change was made at Stripe, but it could NOT be written to this tenant’s audit trail${why}. ` +
        'There is no record here of who made it.',
    };
  }
  if (res.auditRecorded === true && res.auditDegraded === true) {
    return {
      recorded: 'reduced',
      message:
        `Only a reduced audit row could be written${why}. It has who, what, when and the reason, but not the ` +
        'brand and last four, nor which subscriptions and locations were affected.',
    };
  }
  if (res.auditRecorded === true) return { recorded: 'full', message: null };
  return { recorded: 'unknown', message: null };
}

/**
 * The label on the Finance tab's payment-method button.
 *
 * The case this exists for: `defaultPaymentMethod` being null does NOT mean
 * there is nothing on file. Stripe will happily hold two cards with none set as
 * the customer default, and the button used to say "none" while the panel
 * behind it listed both — which reads as "add a card" when the real problem is
 * "choose one". Four states, four sentences: unread, no customer, on file but
 * none charged, and genuinely nothing.
 */
export function paymentMethodButtonLabel(args: {
  loading: boolean;
  /** A transport failure reading the list — distinct from `readable: false`. */
  transportError: boolean;
  data: PaymentMethodsResult | null;
}): { label: string; title: string } {
  const { loading, transportError, data } = args;
  if (loading) {
    return { label: 'Payment method', title: 'Reading what is on file at Stripe…' };
  }
  if (transportError || data?.readable === false) {
    return {
      label: 'Payment method — unread',
      title: 'Stripe could not be read. That is not the same as there being nothing on file.',
    };
  }
  if (!data) {
    return { label: 'Payment method', title: 'Nothing has been read yet.' };
  }
  if (!data.hasCustomer) {
    return {
      label: 'Payment method — no Stripe customer',
      title: 'This tenant has no Stripe customer yet, so no payment method can be stored.',
    };
  }
  const list = data.paymentMethods || [];
  const def = list.find((m) => m.isDefault);
  if (def) {
    return {
      label: describePaymentMethod(def).label,
      title: 'The method Stripe charges. It pays every location on this tenant.',
    };
  }
  if (list.length > 0) {
    return {
      label: `Payment method — ${list.length} on file, none charged`,
      title:
        `${list.length} payment method${list.length === 1 ? ' is' : 's are'} on file, but none is this ` +
        'customer’s default — so Stripe has nothing selected to collect with.',
    };
  }
  return { label: 'Payment method — none', title: 'Nothing is on file for this tenant.' };
}


/** One row of GET /financing — a device finance/lease agreement, now backed by a paged DynamoDB ledger rather than derived live from Stripe. See `financing.ts`. */
export interface FinanceAgreementRow {
  tenantId: string | null;
  tenantName: string | null;
  subscriptionId: string;
  subscriptionStatus: string;
  skuId: string;
  name: string;
  financeType: 'installment' | 'lease' | 'rental';
  termMonths: number | null;
  quantity: number;
  monthlyCents: number;
  totalCents: number | null;
  paidCents: number;
  monthsPaid: number;
  remainingCents: number | null;
  startedAt: number | null;
  currentPeriodEndAt: number | null;
  // Rent option (additive; older billing-service responses may omit them).
  rentalKind?: 'open_ended' | 'fixed_term' | null;
  depositCents?: number;
  minCommitmentMonths?: number;
  cancelAtPeriodEnd?: boolean;
  /**
   * Landed cost per unit, SNAPSHOTTED at sale time by the agreements ledger.
   * Optional because the pre-ledger fallback response does not carry it.
   * Prefer it over joining live off the model: payback means "collected versus
   * what this device cost us WHEN WE SOLD IT", not versus today's price.
   */
  costCents?: number;
}

/** The five stat tiles' figures, computed server-side over the WHOLE book. */
export interface AgreementRollupLike {
  agreements: number;
  totalMonthlyCents: number;
  totalOutstandingCents: number;
  activeRentals: number;
  rentalsPastPayback: number;
  depositsHeldCents: number;
}

export interface CancelFinancingResult {
  subscriptionId: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEndAt: number | null;
  stripeAlreadyEnding: boolean;
  unitsMatched: number;
  unitsCancelled: number;
  unitsAlreadyCancelled: number;
}

/**
 * COMMISSION PLANS — reusable partner commission rule sets.
 * Shapes and arithmetic live in `@platform/shared-types/commissionPlan`; this
 * file never re-derives what a rule pays.
 */
export interface CommissionRuleRow {
  ruleId: string;
  scope: 'software_package' | 'device_offer' | 'subscription' | 'merchant_processing';
  target: string;
  /** Extra ids this same rule also covers. Read via `targetsOf`, never alone. */
  targets?: string[];
  basis: 'percent_of_price' | 'percent_of_margin' | 'flat_per_unit';
  rate: number;
  flatCents: number;
  recurrence: 'once' | 'monthly';
  months?: number | null;
  capCents?: number;
  floorCents?: number;
  note?: string;
}

export interface CommissionPlanRow {
  planId: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'retired';
  /** SERVER-OWNED. Bumps only when rules change, so an event's planVersion stays meaningful. */
  version: number;
  isDefault: boolean;
  rules: CommissionRuleRow[];
  createdAt: number;
  updatedAt: number;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * THE PLATFORM'S RECURRING REVENUE, as answered by billing-service.
 *
 * Two figures, deliberately not reconciled into one:
 *  - `billed` is Stripe's answer — every recurring item on every live
 *    subscription, which is the only place device rentals exist as money.
 *  - `contracted` is composed from the active plan assignments — what we agreed
 *    to charge. It cannot include devices, and says so via `excludesDevices`.
 *
 * Nothing here is derived in the browser. The UI renders these numbers; it does
 * not add them up, and it must never fall back to `tenant.subscriptionCost`,
 * which is one Stripe line item and was the original bug.
 */
export interface RevenueSummaryTenant {
  tenantId: string;
  name: string;
  status: string;
  billedCents: number;
  billedSoftwareCents: number;
  billedDeviceCents: number;
  /** Live money matching neither a package nor a device SKU. */
  billedOtherCents: number;
  /** NULL means this tenant's plan could not be read — not that it is zero. */
  contractedCents: number | null;
  deltaCents: number | null;
  locations: number;
  deviceSubscriptions: number;
  divergent: boolean;
  unread: boolean;
}

export interface RevenueSummary {
  billed: {
    softwareCents: number;
    deviceCents: number;
    otherCents: number;
    totalCents: number;
    /** Live money on a Stripe customer with no tenant row. */
    unattributedCents: number;
  };
  contracted: {
    softwareCents: number;
    addOnCents: number;
    totalCents: number;
    excludesDevices: true;
  };
  counts: {
    tenants: number;
    activeTenants: number;
    billedLocations: number;
    deviceSubscriptions: number;
    subscriptions: number;
  };
  needsAttention: {
    activeWithoutPlan: string[];
    /** Contracted packages disagree with the software Stripe bills. */
    divergentCount: number;
    /** Tenants carrying lines we could not identify as package or device. */
    unmodelledTenantIds: string[];
  };
  divergentTenantIds: string[];
  tenants: RevenueSummaryTenant[];
  errors: { tenantId: string; message: string }[];
  /** TRUE when at least one tenant could not be read — show the figures as a floor. */
  partial: boolean;
  generatedAt: number;
}

export const billingApi = {
  listSkus: () => req<{ items: BillingSkuRow[]; count: number; invalid: string[] }>('/catalog/skus'),

  /** GET /admin/revenue-summary — see `RevenueSummary`. Platform-admin only. */
  getRevenueSummary: () => req<RevenueSummary>('/admin/revenue-summary'),

  /** GET /commission-plans — every plan, default first. `invalid` lists unparseable rows. */
  listCommissionPlans: () =>
    req<{ items: CommissionPlanRow[]; count: number; invalid: string[] }>('/commission-plans'),
  getCommissionPlan: (planId: string) =>
    req<{ plan: CommissionPlanRow }>(`/commission-plans/${encodeURIComponent(planId)}`),
  /**
   * Creates or updates. `version` and `isDefault` demotion are server-owned —
   * never send a version, and expect the server to demote the previous default
   * itself. A rules problem (margin basis on a rental, duplicate target, floor
   * above cap) comes back as a 400 whose message should be shown verbatim.
   */
  upsertCommissionPlan: (plan: Record<string, any>) =>
    req<{ plan: CommissionPlanRow }>('/commission-plans', { method: 'POST', body: JSON.stringify(plan) }),
  /** Refused with 409 for the default plan — promote another first. */
  deleteCommissionPlan: (planId: string) =>
    req<{ deleted: string }>(`/commission-plans/${encodeURIComponent(planId)}`, { method: 'DELETE' }),
  /**
   * One page of the agreements book plus WHOLE-BOOK totals.
   *
   * `count` is this page, not the book — read `totals.agreements` for the book.
   * `totals` is absent only on the pre-ledger fallback response.
   */
  listFinancing: (params?: {
    status?: 'active' | 'ending' | 'closed' | 'all';
    financeType?: 'installment' | 'lease' | 'rental';
    q?: string;
    limit?: number;
    nextToken?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.financeType) qs.set('financeType', params.financeType);
    if (params?.q) qs.set('q', params.q);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.nextToken) qs.set('nextToken', params.nextToken);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<{
      agreements: FinanceAgreementRow[];
      count: number;
      nextToken: string | null;
      totals?: AgreementRollupLike;
      generatedAt: number;
    }>(`/financing${suffix}`);
  },
  /**
   * POST /financing/cancel — ends a RENTAL: Stripe billing stops at period
   * end, hardware-service records the cancellation on the units and flags
   * the device for return. Rentals only — the server refuses anything else.
   * `reason` is required (audited). Safe to re-run after a partial failure.
   */
  cancelFinancing: (body: { subscriptionId: string; skuId?: string; reason: string }) =>
    req<CancelFinancingResult>('/financing/cancel', { method: 'POST', body: JSON.stringify(body) }),
  /**
   * Creates a NEW sku, or updates one whose `skuId` already exists — the SAME
   * endpoint does both (`catalog.ts`'s `upsertSku`). The server reads the
   * existing row itself (by `skuId`) and merges `{ defaults, ...existing,
   * ...body }`, so a caller MAY send a partial body when editing — any field
   * left out survives from the stored row untouched (e.g. an already-minted
   * `stripePriceId` is never wiped by an edit that only changes the amount).
   * A NEW sku has no existing row to fall back on, so its body must carry
   * every field `BillingSkuSchema` requires with no default.
   */
  upsertSku: (sku: Record<string, any>) =>
    req<{ sku: BillingSkuRow }>('/catalog/skus', { method: 'POST', body: JSON.stringify(sku) }),
  /**
   * DELETE /catalog/skus/{skuId} — permanently removes a DEVICE offer. The
   * server refuses anything that is not device_finance, and refuses an offer
   * still being offered (retire first — status must not be 'active'). Callers
   * should additionally keep delete disabled while any agreement references
   * the SKU: the financing book joins name/term through it.
   */
  deleteSku: (skuId: string) =>
    req<{ deleted: boolean; skuId: string }>(`/catalog/skus/${encodeURIComponent(skuId)}`, { method: 'DELETE' }),
  /**
   * Mints (or reuses) a Stripe price for one SKU. Throws with the server's own
   * message — e.g. a zero-amount SKU is refused with a "zero amount" message
   * that should be shown to the operator verbatim.
   *
   * A 200 is NOT proof a price was generated: a SKU whose type never carries a
   * Stripe price comes back `skipped: true` with a `reason`. Check `skipped`
   * before reporting success.
   */
  generatePrice: (skuId: string) =>
    req<GeneratePriceResult>(`/catalog/skus/${encodeURIComponent(skuId)}/price`, { method: 'POST' }),
  /**
   * Bulk-generates prices for every active, unpriced DEVICE SKU. Never throws
   * for a single SKU's failure — inspect `failed`; a non-empty `failed` array
   * is a partial failure, not a success.
   *
   * `skipped` mixes three different situations — use `skippedDetail[].code` to
   * tell them apart. Reporting a `not_priceable` package as "already priced"
   * misinforms the operator about billing infrastructure.
   */
  syncPrices: () => req<SyncPricesResult>('/catalog/prices/sync', { method: 'POST' }),
  /**
   * Reconciles Stripe with this tenant's active PlanAssignment + enabledPackages
   * (design §7). Additive and idempotent — safe to call after every package
   * assign/remove (D8) and safe to press twice from a retry button. Never
   * blocks or undoes the entitlement write it follows; a failure here means
   * the tenant keeps the package and is flagged `billingSyncPending` (D9).
   *
   * The endpoint returns the full result on BOTH 200 (synced, or skipped for
   * D12's no-plan-assignment case) and 409 (billing did not update — the
   * entitlement write already succeeded). A 409 here is a well-formed outcome
   * carrying `errors`, not a bare error string, so it is parsed as data
   * instead of going through the throw-on-`!res.ok` `req()` helper, which
   * would otherwise discard `added`/`removed`/`skipped` and leave only a
   * generic message.
   */
  /**
   * The last entitlement-vs-billing drift sweep (billing-service, nightly).
   *
   * Finds tenants USING a package nothing bills for — the leak left by
   * super-admin-ext's package assignment, which writes entitlements and makes
   * no billing call. Read-only and deliberately a day old: opening a dashboard
   * must not trigger a full-estate scan.
   */
  getBillingDrift: (): Promise<BillingDriftReport> => req<BillingDriftReport>('/billing/drift'),

  /**
   * The drill-down for one tenant. Reads Stripe LIVE, so it is called when an
   * operator opens a tenant and never as part of loading the list.
   */
  getBillingDriftDetail: (tenantId: string): Promise<BillingDriftDetail> =>
    req<BillingDriftDetail>(`/billing/drift/${encodeURIComponent(tenantId)}`),

  syncSubscription: async (tenantId: string): Promise<SyncSubscriptionResult> => {
    const res = await fetch(`${host()}/tenants/${encodeURIComponent(tenantId)}/sync-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
    });
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON */ }
    // 200 and 409 both carry a full SubscriptionSyncResult (identified by the
    // `errors` array every variant includes, even when empty).
    if (body && Array.isArray(body.errors)) return body as SyncSubscriptionResult;
    throw new Error(body?.error || `billing ${res.status}`);
  },

  // -- plan assignments ----------------------------------------------------

  /**
   * The assignment HISTORY, newest first and PAGED (default 50, max 200).
   *
   * This is for showing what was agreed over time, and for finding a resumable
   * draft. It is NOT how you ask what the tenant is on right now — see
   * `getActivePlanAssignment`.
   */
  listPlanAssignments: (tenantId: string, opts: { limit?: number; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit) qs.set('limit', String(opts.limit));
    if (opts.cursor) qs.set('cursor', opts.cursor);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return req<PlanAssignmentListResult>(`/plan-assignments/${encodeURIComponent(tenantId)}${suffix}`);
  },

  /**
   * The ONLY correct way to read what a tenant is billing on RIGHT NOW.
   *
   * Never read the unfiltered first page and filter for `status === 'active'`
   * client-side. Every quote mints a draft with a newer, higher-sorting id, so a
   * tenant carrying more than one page of drafts returns a first page of pure
   * drafts — and the active row, which is further down, is invisible. The
   * client-side filter then finds nothing and the tenant reads as having NO PLAN
   * AT ALL, on a billing surface, behind a 200.
   *
   * `status=active` pages to exhaustion server-side precisely so it cannot miss
   * one. `items[0]` is the newest and is the one to trust; a second row would
   * mean the one-active-assignment invariant broke and is surfaced, not hidden.
   */
  getActivePlanAssignment: (tenantId: string) =>
    req<PlanAssignmentListResult>(`/plan-assignments/${encodeURIComponent(tenantId)}?status=active`),

  /** Creates a draft, or REPLACES one in place when `assignmentId` is set. Never touches Stripe. */
  savePlanAssignmentDraft: (tenantId: string, body: PlanDraftBody) =>
    req<{ assignment: PlanAssignmentRow; created: boolean }>(
      `/plan-assignments/${encodeURIComponent(tenantId)}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Prices a plan WITHOUT persisting anything — the server's own arithmetic, and
   * the only source of any amount the builder displays.
   *
   * Send a `PlanDraftBody` to price an unsaved plan, or `{ assignmentId }` alone
   * to price a stored one (which is how the CURRENT bill is read: compose the
   * active assignment rather than adding numbers up in the browser).
   */
  previewPlanAssignment: (
    tenantId: string,
    body: PlanDraftBody | { assignmentId: string },
    /**
     * `compareToActive` asks the SERVER for the before → after: it composes the
     * assignment in force from the same catalog read and returns `before`,
     * `beforeError` and a package-by-package `changeSummary`.
     *
     * Kept OUT of the draft body on purpose. `buildPlanDraftBody` produces the
     * plan that is previewed, saved and activated byte-for-byte; a display flag
     * inside it would travel to `POST /plan-assignments/{tenantId}` too and
     * change the hash the builder uses to tell a stale total from a current one.
     */
    opts: { compareToActive?: boolean } = {},
  ) =>
    req<PlanComposition>(
      `/plan-assignments/${encodeURIComponent(tenantId)}/preview`,
      {
        method: 'POST',
        body: JSON.stringify(opts.compareToActive ? { ...body, compareToActive: true } : body),
      },
    ),

  /**
   * Promotes a draft to ACTIVE, superseding whatever was active, in one
   * transaction.
   *
   * The server RE-COMPOSES and applies the same gate the preview reported, so a
   * plan with any composition error is refused with a 400 naming each package.
   * That 400 carries the structured `errors`, so it is parsed as data and
   * rethrown as a `PlanActivationRefusal` — a caller that only had the message
   * string could not highlight the offending packages.
   */
  // -- payment methods -----------------------------------------------------

  /**
   * What is on file for this tenant, and what a removal would cost.
   *
   * Always resolves — a Stripe that could not be read comes back
   * `readable: false` with an `error`, NOT as an empty list and not as a throw.
   * Callers must branch on `readable` before saying "no payment method".
   */
  listPaymentMethods: (tenantId: string) =>
    req<PaymentMethodsResult>(`/tenants/${encodeURIComponent(tenantId)}/payment-methods`),

  /**
   * WHAT WE SENT TO STRIPE — the completion's own stored breakdown.
   *
   * The record of what this account was sold, per SKU, as it was at the moment
   * of sale. Not a recomputation: a catalogue edited since must not change the
   * answer, which is the whole reason a verification screen can trust it.
   */
  getOnboardingResult: (tenantId: string) =>
    req<OnboardingResultResult>(`/tenants/${encodeURIComponent(tenantId)}/onboarding-result`),

  /**
   * A client secret so the BROWSER can hand Stripe a bank account (or a card)
   * directly. Nothing about the instrument passes through us.
   */
  createPaymentSetupIntent: (tenantId: string, types?: string[]) =>
    req<{ clientSecret: string; setupIntentId: string }>(
      `/tenants/${encodeURIComponent(tenantId)}/payment-methods/setup-intent`,
      { method: 'POST', body: JSON.stringify(types ? { types } : {}) },
    ),

  /**
   * Add a method, make an existing one the default, or REPLACE one — the
   * server says in `action` which of the three it was.
   *
   * `replacesPaymentMethodId` makes it a replace, and the server attaches the
   * new instrument and repoints the default BEFORE detaching the old one, so a
   * half-failed replace can never leave the tenant with nothing to charge.
   *
   * ONLY an opaque `pm_…` id is ever sent. There is no parameter here for a
   * card number, a CVC, an account number or a routing number, and there must
   * never be one.
   *
   * Two answers are NOT successes and must be read off the result rather than
   * assumed from the 200:
   *  - `defaultError` (with or without `defaultPartial`) — the method is on
   *    file but is not, or is not fully, what gets charged;
   *  - `replaceError` — the old method is still on file.
   * `describeAttachOutcome` turns the combination into one honest sentence.
   *
   * A 409 refusing to make an unverified BANK ACCOUNT the default is a
   * well-formed answer, not a transport failure, so it is rethrown as a
   * `BankVerificationRefusal`. The way forward it names — put the account on
   * file without making it the default — needs the `pm_…` id, which a bare
   * message string would have thrown away.
   */
  attachPaymentMethod: async (
    tenantId: string,
    args: { paymentMethodId: string; makeDefault?: boolean; replacesPaymentMethodId?: string },
  ): Promise<AttachPaymentMethodResult> => {
    const res = await fetch(
      `${host()}/tenants/${encodeURIComponent(tenantId)}/payment-methods`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(args),
      },
    );
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON */ }
    if (res.ok && body?.paymentMethod) return body as AttachPaymentMethodResult;
    if (body?.requiresBankVerification === true && typeof body.error === 'string') {
      const refusal = new Error(body.error) as BankVerificationRefusal;
      refusal.bankVerification = {
        message: body.error,
        setupIntentStatus: String(body.setupIntentStatus || ''),
        // `body.paymentMethodId` is the server's echo of the same opaque id we
        // sent. Falling back to our own copy keeps the way-forward button
        // working rather than losing the id the refusal is about.
        paymentMethodId: String(body.paymentMethodId || args.paymentMethodId),
      };
      throw refusal;
    }
    throw new Error(body?.error || body?.message || `billing ${res.status}`);
  },

  /**
   * Remove a card or bank account.
   *
   * Removing the LAST method while subscriptions are live answers 409 with the
   * consequence spelled out. That 409 is a well-formed ANSWER, not a transport
   * failure, so it is parsed as data and rethrown as a
   * `PaymentMethodRemovalRefusal` carrying the server's own warning — a caller
   * holding only a message string could not show the operator the subscription
   * count, the locations or the grace period, and would end up composing its
   * own version of the rule.
   *
   * Re-call with `acknowledgeNoPaymentMethod: true` AND a `reason` to proceed.
   * Both are stored on the audit trail.
   *
   * A SECOND kind of 409 says the removal was refused because the replacement
   * could not be promoted first. Nothing was detached — but the customer
   * default may already have moved, because `setDefaultPaymentMethod` writes it
   * before repointing subscriptions. That is rethrown as a
   * `PaymentMethodRemovalFailure` so the caller can say which of the two
   * happened and reload when the default did move.
   */
  removePaymentMethod: async (
    tenantId: string,
    paymentMethodId: string,
    opts: { acknowledgeNoPaymentMethod?: boolean; reason?: string } = {},
  ): Promise<RemovePaymentMethodResult> => {
    const res = await fetch(
      `${host()}/tenants/${encodeURIComponent(tenantId)}/payment-methods/${encodeURIComponent(paymentMethodId)}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(opts),
      },
    );
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON */ }
    if (res.ok && body?.removed) return body as RemovePaymentMethodResult;
    // A refused removal whose PROMOTION got part-way. Nothing was detached, but
    // the customer default may already have moved — a caller holding only a
    // message string could not know to reload, and would go on showing the old
    // method as the charged one.
    if (typeof body?.customerDefaultMoved === 'boolean') {
      const failure = new Error(body.error || 'the payment method was not removed') as PaymentMethodRemovalFailure;
      failure.removalFailure = {
        customerDefaultMoved: body.customerDefaultMoved === true,
        promotedPaymentMethodId: body.promotedPaymentMethodId || null,
        intendedPromotionPaymentMethodId: body.intendedPromotionPaymentMethodId || null,
        repointedSubscriptionIds: Array.isArray(body.repointedSubscriptionIds) ? body.repointedSubscriptionIds : [],
        promoteError: typeof body.promoteError === 'string' ? body.promoteError : undefined,
        auditRecorded: typeof body.auditRecorded === 'boolean' ? body.auditRecorded : undefined,
        auditDegraded: body.auditDegraded === true ? true : undefined,
        auditError: typeof body.auditError === 'string' ? body.auditError : undefined,
      };
      throw failure;
    }
    if (body?.requiresAcknowledgement && typeof body.warning === 'string') {
      const refusal = new Error(body.warning) as PaymentMethodRemovalRefusal;
      refusal.refusal = {
        warning: body.warning,
        requiresReason: body.requiresReason === true,
        liveSubscriptionCount: Number(body.liveSubscriptionCount || 0),
        affectedLocationIds: Array.isArray(body.affectedLocationIds) ? body.affectedLocationIds : [],
        graceDays: Number(body.graceDays || 0),
      };
      throw refusal;
    }
    throw new Error(body?.error || body?.message || `billing ${res.status}`);
  },

  activatePlanAssignment: async (tenantId: string, assignmentId: string): Promise<PlanActivationResult> => {
    let res: Response;
    try {
      res = await fetch(`${host()}/plan-assignments/${encodeURIComponent(tenantId)}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ assignmentId }),
      });
    } catch (e: any) {
      // The request never got a response. It may still have been RECEIVED and
      // applied — publishing is not a read — so this is indeterminate, never
      // "nothing happened".
      const failure = new Error(
        `the publish request did not complete (${e?.message || 'network failure'}).`,
      ) as ActivationTransportError;
      failure.status = 0;
      failure.indeterminate = true;
      throw failure;
    }
    let body: any = null;
    try { body = await res.json(); } catch { /* non-JSON */ }
    if (res.ok && body?.assignment) return body as PlanActivationResult;
    if (body && Array.isArray(body.errors) && body.errors.length) {
      const refusal = new Error(body.error || 'this plan cannot be priced') as PlanActivationRefusal;
      refusal.refusal = { assignmentId: body.assignmentId, errors: body.errors, warnings: body.warnings || [] };
      throw refusal;
    }
    // `message` as well as `error`: an API Gateway timeout answers
    // `{"message":"Endpoint request timed out"}`, which this service never
    // wrote, and dropping it would leave the operator with a bare "billing 504".
    const failure = new Error(
      body?.error || body?.message || `billing ${res.status}`,
    ) as ActivationTransportError;
    failure.status = res.status;
    // 5xx only. A 4xx is the SERVER'S OWN answer about what happened ("already
    // active", "not found", a refusal) and must not be dressed up as a mystery.
    failure.indeterminate = res.status >= 500;
    throw failure;
  },
};

/** How an offer reads to a human. Mirrors the wording used on the deal card. */
/**
 * Does this SKU still NEED a Stripe price?
 *
 * Only `device_finance` SKUs get one FROM THEIR CATALOG ROW (spec §13/B2).
 * Software bills at the NET a plan assignment composes, and billing-service
 * mints that price at subscription time instead. A package or plan with no
 * Stripe price on its SKU row is therefore CORRECT, not broken, and must never
 * be surfaced as a red gap with a "Generate" button that comes back skipped.
 *
 * `undefined` priceable means the server predates the field — fall back to the
 * old behaviour rather than guessing.
 */
export const needsStripePrice = (s: Pick<BillingSkuRow, 'hasPrice' | 'priceable'>): boolean =>
  !s.hasPrice && s.priceable !== false;

/** A SKU billed entirely on our side — no Stripe price is expected, ever. */
export const billedInternally = (s: Pick<BillingSkuRow, 'priceable'>): boolean =>
  s.priceable === false;

/**
 * Split a bulk sync's `skipped` list into the distinct facts it conflates.
 *
 * Counting them as one number is how an operator gets told a package is
 * "already priced" when in truth it will never be priced at all — misinformation
 * about money infrastructure. When the server sent no detail (an older
 * billing-service) the old meaning is preserved rather than invented.
 */
export function countSkips(res: Pick<SyncPricesResult, 'skipped' | 'skippedDetail'>):
  { notPriceable: number; alreadyPriced: number; deprecated: number } {
  const detail = res.skippedDetail;
  if (!detail) return { notPriceable: 0, alreadyPriced: res.skipped.length, deprecated: 0 };
  return {
    notPriceable: detail.filter(d => d.code === 'not_priceable').length,
    alreadyPriced: detail.filter(d => d.code === 'already_priced').length,
    deprecated: detail.filter(d => d.code === 'deprecated').length,
  };
}

export function offerLabel(sku: Pick<BillingSkuRow, 'saleMode' | 'amountCents' | 'termMonths'>): string {
  const money = `$${(sku.amountCents / 100).toFixed(2)}`;
  switch (sku.saleMode) {
    case 'outright': return `${money} one-time`;
    case 'installment': return `${money}/mo for ${sku.termMonths ?? '?'} months`;
    case 'rental': return `${money}/mo, no term`;
    case 'included': return 'Included free';
    case 'lease': return `${money}/mo lease`;
    default: return money;
  }
}

// ---------------------------------------------------------------------------
// CONVERTING A LEAD (Task 8b) — the OPERATOR's onboarding path
// ---------------------------------------------------------------------------
//
// Two paths create a tenant. The self-serve wizard finishes through
// `billing-service`'s `POST /onboarding/complete`, which writes a
// `PlanAssignment` atomically with the completion — the internal record of what
// the customer was sold and how the price was arrived at. The operator path
// (`/command-center` → `POST /onboard` in `tenants-service`) historically wrote
// no such record at all: it sent a bare `priceId`/`priceAmount` and nothing
// said which packages, at which discount, produced that number.
//
// These helpers are the operator path's half of the convergence. They are pure
// on purpose: this repo runs plain vitest but has no DOM renderer, so anything
// left inside the page component cannot be tested at all.
//
// ---------------------------------------------------------------------------
// THE ORDER OF EVENTS, AND WHY IT IS THE WAY IT IS
// ---------------------------------------------------------------------------
//
// `POST /onboard` answers 202 and starts `OnboardingStateMachine-${stage}`;
// `CreateTenant`, its FIRST step, mints `tenant_${Date.now()}` itself. So at
// the moment the operator presses Convert there is no `tenantId`, and the
// PlanAssignment API is tenant-scoped (`POST /plan-assignments/{tenantId}`) —
// there is nothing to key a draft under. The assignment can therefore only be
// created AFTER onboarding starts and the tenant row appears.
//
// The governing principle is the wizard's: **right or absent, never wrong.**
// If any step of that second phase cannot be completed — the tenant cannot be
// identified, the plan cannot be priced, the browser is closed — the tenant
// simply has no assignment, which the Finance tab renders as "no plan" and
// every sync path skips (D12). That is strictly better than a wrong price, and
// it is why nothing here invents a half-written record to avoid an absent one.

/**
 * The subset of a DEAL this path reads.
 *
 * Read from the ADMIN endpoints only (`GET /admin/deals`). The public ones
 * deliberately no longer return `packageDiscounts` or `packagesTotals`: they
 * were exposing per-package discount values and free-text negotiation `reason`
 * notes on unauthenticated URLs, and a `reason` must never reach a
 * customer-visible surface.
 */
export interface DealLike {
  id?: string;
  code?: string;
  name?: string;
  productId?: string;
  /** The software packages the deal includes — the assignment's `planPackageIds`. */
  packageIds?: string[];
  /**
   * packageId -> discount, in the EXACT `DiscountSchema` shape a PlanAssignment
   * uses. That is deliberate (`deal.ts`): a deal becomes an assignment by COPY,
   * not by translation, so there is no conversion here to round differently.
   */
  packageDiscounts?: Record<string, PlanDiscount>;
  /** Device SKUs offered with the deal. The API keeps both names in lockstep. */
  deviceSkuIds?: string[];
  devicePackageIds?: string[];
  /** `skuId -> the deal's terms for that device finance option`. Absent on a deal that predates them. */
  deviceTerms?: Record<string, DealDeviceTerm>;
  trialDays?: number;
  /** @deprecated superseded by `priceCents`; still read for deals that predate it. */
  stripePriceId?: string;
  /** @deprecated superseded by `priceCents`. */
  monthlyAmount?: number;
  priceCents?: number;
}

/** The plan being built for a tenant that does not exist yet. */
export interface OnboardingPlanDraft {
  planPackageIds: string[];
  perPackage: Record<string, DiscountInput>;
  overall: DiscountInput;
  /** Whole days, already normalised through `trialDaysFor`. 0 = no trial. */
  trialDays: number;
}

/**
 * A deal, as the PLAN a tenant is about to be sold.
 *
 * `deal.packageDiscounts` and `deal.trialDays` reach no `PlanAssignment` today
 * — the copying half was never built, and this is the operator-facing creation
 * path, so it is one of the two places that must do it. Both are copied, not
 * translated: `discountToInput` is the exact inverse of `toDiscount`, and both
 * sides of the copy are the same `DiscountSchema`.
 *
 * A discount naming a package the deal does NOT include is dropped, because
 * `POST /plan-assignments/{tenantId}` refuses one ("the discount would never
 * apply", 400) and because a reduction attributed to something nobody is
 * buying explains nothing.
 *
 * There is no overall discount on a deal, so the assignment starts without one
 * — the operator may still add one before converting.
 */
export function dealToPlanDraft(deal?: DealLike | null): OnboardingPlanDraft {
  const planPackageIds = Array.from(new Set((deal?.packageIds || []).filter(Boolean)));
  const perPackage: Record<string, DiscountInput> = {};
  for (const packageId of planPackageIds) {
    perPackage[packageId] = discountToInput(deal?.packageDiscounts?.[packageId]);
  }
  return {
    planPackageIds,
    perPackage,
    overall: { ...BLANK_DISCOUNT },
    trialDays: 0, // retired 2026-08-28 — a deal's stored number is never inherited
  };
}

/**
 * The device SKUs a deal offers, under whichever of the two names it carries.
 *
 * `devicePackageIds` (the newer name) WINS over `deviceSkuIds` rather than
 * being unioned with it — that is the rule `super-admin-ext`'s
 * `resolveDeviceSkuIds` applies when storing, and a union here would have a
 * device the operator removed reappear because the older field still named it.
 */
export const dealDeviceSkuIds = (deal?: DealLike | null): string[] => {
  if (Array.isArray(deal?.devicePackageIds)) return Array.from(new Set(deal!.devicePackageIds));
  if (Array.isArray(deal?.deviceSkuIds)) return Array.from(new Set(deal!.deviceSkuIds));
  return [];
};

// ---------------------------------------------------------------------------
// Device deal terms, as a FORM holds them
// ---------------------------------------------------------------------------

/**
 * One device finance option's deal terms while an operator is editing them.
 *
 * Everything numeric is a STRING, as typed — the same posture `DealFormValues`
 * takes with `priceDollars`. Parsing happens once, in `toDeviceTerm`, so a
 * half-typed "1." never becomes a number mid-keystroke and a field an operator
 * cleared never silently reverts to a stored value.
 */
export interface DeviceTermInput {
  discount: DiscountInput;
  takenFrom: DeviceDiscountTarget;
  covers: DeviceCoverMode;
  /** Units covered, as typed. Only read when `covers === 'first_n'`. */
  coveredUnits: string;
  /**
   * The deal's down payment, in DOLLARS as typed. Blank means "leave the SKU's
   * own" — which is NOT the same as "0", the explicit $0-down offer.
   */
  downPaymentDollars: string;
  showOneTimeInPreview: boolean;
}

export const BLANK_DEVICE_TERM: DeviceTermInput = {
  discount: { ...BLANK_DISCOUNT },
  takenFrom: 'monthly',
  covers: 'each',
  coveredUnits: '1',
  downPaymentDollars: '',
  showOneTimeInPreview: true,
};

/** What is wrong with these terms, in the operator's words — or null. */
export function deviceTermError(input?: DeviceTermInput): string | null {
  if (!input) return null;
  const discountProblem = discountInputError(input.discount);
  if (discountProblem) return discountProblem;

  if (input.covers === 'first_n') {
    const raw = (input.coveredUnits ?? '').trim();
    const n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 1) {
      return 'Enter how many devices this offer covers (1 or more).';
    }
  }

  const down = (input.downPaymentDollars ?? '').trim();
  if (down) {
    const n = Number(down);
    if (!Number.isFinite(n)) return 'Down payment: enter a number.';
    if (n < 0) return 'Down payment: a down payment can never be negative.';
  }
  return null;
}

/**
 * Form terms into the stored shape, or `undefined` when they change no price.
 *
 * An "empty" term — a drawer opened and closed — must produce NOTHING rather
 * than an object full of defaults: a stored `{ covers: 'each' }` would make
 * every consumer treat the device as deal-priced when it is billing at list,
 * and `deviceTermIsPriced` is what both sides use to tell those apart.
 */
export function toDeviceTerm(input?: DeviceTermInput): DealDeviceTerm | undefined {
  if (!input || deviceTermError(input)) return undefined;
  const discount = toDiscount(input.discount);
  const down = (input.downPaymentDollars ?? '').trim();
  const downPaymentCents = down ? Math.round(Number(down) * 100) : undefined;
  if (!discount && downPaymentCents === undefined) return undefined;

  return {
    ...(discount ? { discount } : {}),
    // Only meaningful alongside a discount — a down-payment override is not
    // "taken from" anywhere, it replaces a figure outright.
    ...(discount ? { takenFrom: input.takenFrom } : {}),
    ...(input.covers === 'first_n'
      ? { covers: 'first_n' as const, coveredUnits: Math.round(Number(input.coveredUnits)) }
      : { covers: 'each' as const }),
    ...(downPaymentCents !== undefined ? { downPaymentCents } : {}),
    ...(input.showOneTimeInPreview ? {} : { showOneTimeInPreview: false }),
  };
}

// ---------------------------------------------------------------------------
// The unified offer, as a FORM holds it
// ---------------------------------------------------------------------------

/**
 * ONE line's offer while an operator is editing it — software or hardware.
 *
 * Every number is a STRING as typed, the same posture `priceDollars` takes: a
 * half-typed "1." never becomes a number mid-keystroke, and a cleared field
 * never silently reverts to a stored value.
 *
 * `promoEnabled` is explicit rather than inferred from a blank value, because
 * `0` is a REAL promotional price — "free for 3 months" — and inferring "off"
 * from a falsy number would make the commonest promotion inexpressible.
 */
export interface OfferTermInput {
  discount: DiscountInput;
  takenFrom: OfferTarget;
  promoEnabled: boolean;
  /** `fixed` = an absolute price during the promotion; `percent` = a reduction. */
  promoMode: 'fixed' | 'percent';
  /** Dollars (fixed) or percent, as typed. */
  promoValue: string;
  promoMonths: string;
  covers: OfferCoverMode;
  coveredUnits: string;
  /**
   * HOW MANY LOCATIONS the offer reaches, as typed. BLANK means every one,
   * which is the norm and is why the empty string — not '1' — is the default.
   *
   * A different question from `covers`/`coveredUnits`, which asks how many UNITS
   * at a location. "The first two terminals at each of your first two stores"
   * needs both, and collapsing them makes that inexpressible.
   *
   * The field is in `DealOfferTermSchema`, `deviceDealTiers` prices from it and
   * billing-service bills from it — but until now nothing could SET it, so the
   * capability existed everywhere except where an operator could reach it.
   */
  coveredLocations: string;
  /** Dollars as typed. Blank keeps the catalog's own; '0' is the "$0 down" offer. */
  downPaymentDollars: string;
  showOneTimeInPreview: boolean;
  reason: string;
}

export const BLANK_OFFER_TERM: OfferTermInput = {
  discount: { ...BLANK_DISCOUNT },
  takenFrom: 'monthly',
  promoEnabled: false,
  promoMode: 'fixed',
  promoValue: '',
  promoMonths: '12',
  covers: 'each',
  coveredUnits: '1',
  // Blank = every location. An offer that silently reached only the first store
  // would be the worst possible default.
  coveredLocations: '',
  downPaymentDollars: '',
  showOneTimeInPreview: true,
  reason: '',
};

/**
 * What is wrong with an offer, in the operator's words — or null.
 *
 * `shape` says which halves of the control are live for this line, so a
 * package is never asked about a down payment it cannot have.
 */
export function offerTermError(
  input: OfferTermInput | undefined,
  shape: { hasMonthly: boolean; hasOneTime: boolean },
): string | null {
  if (!input) return null;

  const discountProblem = discountInputError(input.discount);
  if (discountProblem) return discountProblem;

  if (input.promoEnabled) {
    const raw = (input.promoValue ?? '').trim();
    if (!raw) {
      return input.promoMode === 'fixed'
        ? 'Enter what the customer pays during the limited-time period (0 for free).'
        : 'Enter the percentage off during the limited-time period.';
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return 'Limited-time price: enter a number.';
    if (n < 0) return 'Limited-time price: a price cannot be negative.';
    if (input.promoMode === 'percent' && n > 100) return 'A percentage must be between 0 and 100.';

    const months = (input.promoMonths ?? '').trim();
    const monthsNumber = Number(months);
    if (!months || !Number.isInteger(monthsNumber) || monthsNumber < 1) {
      return 'Enter how many months the limited-time price runs for (1 or more).';
    }
  }

  if (input.covers === 'first_n') {
    const raw = (input.coveredUnits ?? '').trim();
    const n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 1) return 'Enter how many devices this offer covers (1 or more).';
  }

  // Blank is VALID and means every location; only a typed value is checked.
  const locations = (input.coveredLocations ?? '').trim();
  if (locations) {
    const n = Number(locations);
    if (!Number.isInteger(n) || n < 1) {
      return 'Locations covered: enter a whole number of locations (1 or more), or leave it blank for every location.';
    }
  }

  if (shape.hasOneTime) {
    const down = (input.downPaymentDollars ?? '').trim();
    if (down) {
      const n = Number(down);
      if (!Number.isFinite(n)) return 'Down payment: enter a number.';
      if (n < 0) return 'Down payment: a down payment can never be negative.';
    }
  }
  return null;
}

/**
 * Form offer into the stored shape, or `undefined` when it changes no price.
 *
 * An "empty" offer produces NOTHING rather than an object full of defaults: a
 * stored `{ covers: 'each' }` would make every consumer treat the line as
 * deal-priced when it is billing at list, and `offerIsPriced` is what both
 * sides use to tell those apart.
 */
export function toOfferTerm(
  input: OfferTermInput | undefined,
  shape: { hasMonthly: boolean; hasOneTime: boolean; hasQuantity: boolean },
): DealOfferTerm | undefined {
  if (!input || offerTermError(input, shape)) return undefined;

  const discount = toDiscount(input.discount);
  const promo = input.promoEnabled && (input.promoValue ?? '').trim()
    ? {
      ...(input.promoMode === 'fixed'
        ? { fixedPriceCents: Math.round(Number(input.promoValue) * 100) }
        : { discount: { percentOff: Number(input.promoValue) } }),
      months: Math.round(Number(input.promoMonths)),
    }
    : undefined;
  const down = shape.hasOneTime ? (input.downPaymentDollars ?? '').trim() : '';
  const downPaymentCents = down ? Math.round(Number(down) * 100) : undefined;
  // Location reach is a property of a QUANTIFIED line — a software package is
  // one per location by definition, so the question does not arise for it.
  const locations = shape.hasQuantity ? (input.coveredLocations ?? '').trim() : '';
  const coveredLocations = locations ? Math.round(Number(locations)) : undefined;

  if (!discount && !promo && downPaymentCents === undefined) return undefined;

  const reason = (input.reason ?? '').trim();
  return {
    ...(discount ? { discount } : {}),
    takenFrom: input.takenFrom,
    ...(promo ? { promo } : {}),
    // Quantity scope is meaningless for a package, which is always one.
    ...(shape.hasQuantity
      ? (input.covers === 'first_n'
        ? { covers: 'first_n' as const, coveredUnits: Math.round(Number(input.coveredUnits)) }
        : { covers: 'each' as const })
      : {}),
    // OMITTED, never sent as a number, when the offer reaches everywhere —
    // `termAppliesAtLocation` reads an absent value as "every location", so a
    // stored figure would have to be a real limit rather than a default.
    ...(coveredLocations !== undefined ? { coveredLocations } : {}),
    ...(downPaymentCents !== undefined ? { downPaymentCents } : {}),
    ...(shape.hasOneTime && !input.showOneTimeInPreview ? { showOneTimeInPreview: false } : {}),
    ...(reason ? { reason } : {}),
  };
}

/** A STORED offer back into the shape a form holds — the exact inverse of `toOfferTerm`. */
export function offerTermToInput(term?: DealOfferTerm): OfferTermInput {
  if (!term) return { ...BLANK_OFFER_TERM, discount: { ...BLANK_DISCOUNT } };
  const promo = term.promo;
  return {
    discount: discountToInput(term.discount),
    takenFrom: term.takenFrom || 'monthly',
    promoEnabled: Boolean(promo),
    promoMode: promo && typeof promo.fixedPriceCents === 'number' ? 'fixed' : promo ? 'percent' : 'fixed',
    promoValue: promo
      // `centsToDollarString`, NOT a falsy check: a stored 0 is "free" and must
      // round-trip as "0.00" rather than come back blank and switched off.
      ? (typeof promo.fixedPriceCents === 'number'
        ? centsToDollarString(promo.fixedPriceCents)
        : String(promo.discount?.percentOff ?? ''))
      : '',
    promoMonths: String(promo?.months ?? 12),
    covers: term.covers || 'each',
    coveredUnits: String(term.coveredUnits ?? 1),
    // Absent stays BLANK, not '1': blank is "every location", and seeding a 1
    // would silently narrow every stored offer to the first store on next save.
    coveredLocations: typeof term.coveredLocations === 'number' ? String(term.coveredLocations) : '',
    downPaymentDollars: typeof term.downPaymentCents === 'number'
      ? centsToDollarString(term.downPaymentCents)
      : '',
    showOneTimeInPreview: term.showOneTimeInPreview !== false,
    reason: term.reason || '',
  };
}

// ---------------------------------------------------------------------------
// Introductory pricing, as a FORM holds it
// ---------------------------------------------------------------------------

/**
 * A package's introductory price while an operator is editing it.
 *
 * Both fields are STRINGS as typed, the same posture `priceDollars` takes: a
 * half-typed "1." never becomes a number mid-keystroke, and a field cleared to
 * blank never silently reverts to a stored value.
 *
 * `enabled` is explicit rather than inferred from a blank price, because `0` is
 * a REAL introductory price — "free for 3 months" — and inferring "off" from a
 * falsy value would make the most common promotion impossible to express.
 */
export interface PriceIntroInput {
  enabled: boolean;
  /** What the customer pays during the intro, in DOLLARS as typed. '' = not set. */
  introPriceDollars: string;
  /** How many months it runs for, as typed. */
  introMonths: string;
  /** Internal — negotiation notes, never customer-facing. */
  reason?: string;
}

export const BLANK_PRICE_INTRO: PriceIntroInput = {
  enabled: false, introPriceDollars: '', introMonths: '12', reason: '',
};

/** What is wrong with an introductory price, in the operator's words — or null. */
export function priceIntroError(input?: PriceIntroInput): string | null {
  if (!input || !input.enabled) return null;

  const price = (input.introPriceDollars ?? '').trim();
  if (!price) return 'Enter what the customer pays during the introductory period (0 for free).';
  const priceNumber = Number(price);
  if (!Number.isFinite(priceNumber)) return 'Introductory price: enter a number.';
  if (priceNumber < 0) return 'Introductory price: a price cannot be negative.';

  const months = (input.introMonths ?? '').trim();
  const monthsNumber = Number(months);
  if (!months || !Number.isInteger(monthsNumber) || monthsNumber < 1) {
    return 'Enter how many months the introductory price runs for (1 or more).';
  }
  return null;
}

/**
 * Form intro into the stored shape, or `undefined` when there is no offer.
 *
 * An intro that is switched off, or one that does not validate, produces
 * NOTHING rather than a partial object: a stored intro with a missing month
 * count is a schedule nobody can build, and `PriceIntroSchema` refuses it
 * anyway.
 */
export function toPriceIntro(input?: PriceIntroInput): PriceIntro | undefined {
  if (!input || !input.enabled || priceIntroError(input)) return undefined;
  const reason = (input.reason ?? '').trim();
  return {
    introPriceCents: Math.round(Number(input.introPriceDollars) * 100),
    introMonths: Math.round(Number(input.introMonths)),
    ...(reason ? { reason } : {}),
  };
}

/** A STORED intro back into the shape a form holds — the exact inverse of `toPriceIntro`. */
export function priceIntroToInput(intro?: PriceIntro): PriceIntroInput {
  if (!intro) return { ...BLANK_PRICE_INTRO };
  return {
    enabled: true,
    // `centsToDollarString` NOT a falsy check: a stored 0 is "free", and it
    // must round-trip as "0.00" rather than come back blank and switched off.
    introPriceDollars: centsToDollarString(intro.introPriceCents),
    introMonths: String(intro.introMonths),
    reason: intro.reason || '',
  };
}

/** A STORED term back into the shape a form holds — the exact inverse of `toDeviceTerm`. */
export function deviceTermToInput(term?: DealDeviceTerm): DeviceTermInput {
  if (!term) return { ...BLANK_DEVICE_TERM, discount: { ...BLANK_DISCOUNT } };
  return {
    discount: discountToInput(term.discount),
    takenFrom: term.takenFrom || 'monthly',
    covers: term.covers || 'each',
    coveredUnits: String(term.coveredUnits ?? 1),
    // `?? ''` NOT `|| ''`: a stored 0 is the "$0 down" offer and must survive a
    // round-trip through the form as "0.00", not come back as blank.
    downPaymentDollars: typeof term.downPaymentCents === 'number'
      ? centsToDollarString(term.downPaymentCents)
      : '',
    showOneTimeInPreview: term.showOneTimeInPreview !== false,
  };
}

/**
 * The sale modes that put a device on a RECURRING subscription line — exactly
 * the complement of `outright` and `included` in `SALE_MODES`, which is how
 * `POST /onboarding/complete` splits them: `included` creates no line, an
 * `outright` device goes on its own one-time invoice, and everything else is
 * `billing.recurring`, which is what its trial guard looks at.
 *
 * A device SKU carrying NO `saleMode` is absent from this set and is therefore
 * never warned about. That is correct rather than a gap: such a SKU cannot be
 * ordered at all ("it cannot be ordered until the catalog says how it is
 * sold"), so it can never end up on a subscription beside a trial.
 */
export const FINANCED_SALE_MODES: ReadonlySet<string> = new Set(['installment', 'lease', 'rental']);

/*
  THE TRIAL HELPERS ARE GONE (2026-08-28).

  `trialDeviceFinanceWarning`, `trialDeviceFinanceNote`, `trialDayOptions` and
  `TRIAL_DAYS_CEILING` lived here. They existed to let an operator pick a trial
  length and to warn that a Stripe trial pauses billing for the WHOLE
  subscription — so a location's software and its financed device could not
  share one. Trials are retired: an offer's free period is expressed as price
  phases on the packages and devices it covers, which can zero the software
  while the hardware keeps billing. There is no length to pick and no conflict
  left to warn about.
*/

export interface OnboardPayloadInput {
  /** Present when a LEAD is being converted; absent for a tenant created from scratch. */
  leadId?: string;
  businessName: string;
  ownerName?: string;
  ownerEmail: string;
  deal: DealLike;
  /**
   * The deal's effective price in integer cents, resolved by the caller through
   * `effectiveDealPriceCents` (`dealPricing.ts`) — the same precedence
   * `super-admin-ext` applies, so this and every server read agree about which
   * of `priceCents`/`monthlyAmount` is in force.
   */
  priceAmountCents: number;
  /**
   * The trial actually agreed, in whole days. It is the ASSIGNMENT'S value, not
   * the deal's: where the two disagree the assignment wins (task-8a's own
   * ruling), and sending the assignment's number here is what stops them
   * disagreeing at all — `CreateTenant` stamps `trialDays`, `trialEndDate` and
   * `subscriptionStatus` on the tenant row from THIS field.
   */
  trialDays: number;
  /**
   * The plan assignment this tenant is being onboarded onto, when one can be
   * named. For a brand-new tenant it cannot: `CreateTenant` mints the
   * `tenantId` inside the state machine this call starts, so no tenant-scoped
   * assignment exists yet (see the section header). The field is wired
   * end-to-end regardless, and omitted — never blank, never invented — when
   * there is nothing to name.
   */
  planAssignmentId?: string | null;
}

/**
 * The `POST /onboard` body.
 *
 * EVERY field the operator path has always sent is still sent, under the same
 * name and with the same meaning (D11): `leadId`, `businessName`, `ownerName`,
 * `ownerEmail`, `priceId`, `priceAmount`, `dealCode`, `trialDays`,
 * `productType`, `enabledPackages`. `priceId`/`priceAmount` are carried for
 * backward compatibility and are NOT the source of the price once an
 * assignment exists — the assignment's composed net is.
 */
export function buildOnboardPayload(input: OnboardPayloadInput): Record<string, any> {
  const deal = input.deal || {};
  const planAssignmentId = (input.planAssignmentId || '').trim();
  return {
    ...(input.leadId ? { leadId: input.leadId } : {}),
    businessName: input.businessName,
    ownerName: input.ownerName,
    ownerEmail: input.ownerEmail,
    priceId: deal.stripePriceId,
    // Cents. `createTenant` stores it as `subscriptionCost` unchanged.
    priceAmount: input.priceAmountCents,
    dealCode: deal.code,
    // Always 0. Trials were retired 2026-08-28; the field is still sent so an
    // older handler reading it sees "no trial" rather than nothing at all.
    trialDays: 0,
    productType: deal.productId || 'agenticpos',
    enabledPackages: deal.packageIds || [],
    ...(planAssignmentId ? { planAssignmentId } : {}),
  };
}

/**
 * Why `POST /onboard` would refuse this, in the operator's words — or null.
 *
 * The handler answers 400 when there is neither a `planAssignmentId` nor a
 * price, and that 400 names fields rather than the thing to fix. A deal priced
 * at 0 with no Stripe price is the case that reaches it: legitimate as a deal,
 * but not something this endpoint can start a tenant from while the assignment
 * can only be written afterwards.
 */
export function onboardBlockReason(args: {
  priceId?: string;
  priceAmountCents: number;
  planAssignmentId?: string | null;
}): string | null {
  if ((args.planAssignmentId || '').trim()) return null;
  if ((args.priceId || '').trim()) return null;
  if (args.priceAmountCents > 0) return null;
  return 'This deal has no Stripe price and no monthly amount, so onboarding has nothing to price the tenant at. Give the deal a price (or a Stripe price) before converting.';
}

export type OnboardedTenantLookup =
  | { status: 'found'; tenantId: string }
  | { status: 'pending' }
  | { status: 'ambiguous'; tenantIds: string[] }
  /** The pre-request snapshot could not be read, so no match may be trusted. */
  | { status: 'unusable' };

/**
 * Why the plan was not recorded when the pre-request tenant snapshot failed.
 * Exported so the panel and this module say the same thing about it.
 */
export const ONBOARD_TENANT_SNAPSHOT_UNUSABLE =
  'The tenant list could not be read immediately before the request, so the set of tenants that already existed is not known — only whatever this page happened to be holding, which may be stale. The new tenant is identified as the one absent from that set, so a short or stale set makes an OLDER tenant carrying this owner\'s address answer to the match just as well, and a plan on the wrong tenant is a wrong price. Nothing was written.';

/**
 * WHICH TENANT DID THIS ONBOARDING JUST CREATE?
 *
 * The obvious answer — read it back off the lead — does not work: the state
 * machine's `UpdateLeadStatus` step sets `status: 'onboarded'` and NOTHING
 * ELSE. No step of `OnboardingStateMachine` writes `onboardedTenantId`
 * anywhere; that field exists only in this repo's TypeScript types, inherited
 * from a mock database in the old platform-app. So the lead cannot answer it.
 *
 * What can: the tenant row itself. `CreateTenant` writes `ownerEmail` exactly
 * as it was submitted, so the tenant we just asked for is the one that is BOTH
 * new (absent from the snapshot taken immediately before the request) and
 * carries this owner's address.
 *
 * Deliberately NOT time-based. Comparing `tenant.createdAt` — a Lambda clock —
 * against the browser's `Date.now()` would make the identification depend on
 * two clocks agreeing; a set difference depends on neither.
 *
 * Four answers, because there are four situations:
 *  - `pending`    — it has not appeared yet. Keep looking; the machine is async.
 *  - `found`      — exactly one. Safe to write this tenant's plan.
 *  - `ambiguous`  — more than one new tenant carries this address, so nobody
 *                   can say which one was sold this plan. NOTHING is written:
 *                   an assignment on the wrong tenant is a wrong price, and
 *                   absent beats wrong.
 *  - `unusable`   — the snapshot of "who already existed" could not be taken,
 *                   so the set difference this identification rests on does not
 *                   exist. FEWER known ids make the match LOOSER, never
 *                   stricter: a tenant that was already there, carrying the
 *                   same owner address, would answer to it. Refused outright
 *                   rather than made on a shorter list.
 */
export function matchOnboardedTenant(args: {
  /** Tenant ids that existed BEFORE the onboarding request was sent. */
  knownTenantIds: Iterable<string>;
  /** The tenant list as it stands now. */
  tenants: { id?: string; ownerEmail?: string }[];
  ownerEmail: string;
  /**
   * Whether `knownTenantIds` is the COMPLETE set of tenants that existed before
   * the request. Pass `false` when the read that built it failed: the ids the
   * page happened to hold are not that set, and matching against them is how a
   * pre-existing tenant becomes eligible to be mistaken for the new one.
   */
  snapshotComplete?: boolean;
}): OnboardedTenantLookup {
  if (args.snapshotComplete === false) return { status: 'unusable' };
  const known = new Set(args.knownTenantIds);
  const wanted = (args.ownerEmail || '').trim().toLowerCase();
  if (!wanted) return { status: 'pending' };
  const matches = (args.tenants || [])
    .filter((t) => !!t?.id && !known.has(t.id!))
    .filter((t) => (t.ownerEmail || '').trim().toLowerCase() === wanted)
    .map((t) => t.id!);
  const unique = Array.from(new Set(matches));
  if (unique.length === 0) return { status: 'pending' };
  if (unique.length === 1) return { status: 'found', tenantId: unique[0] };
  return { status: 'ambiguous', tenantIds: unique.sort() };
}

/**
 * Why this composition must NOT be activated — or null.
 *
 * The same two gates the plan builder applies before Publish, and for the same
 * reasons: a package that cannot be priced is a data gap the server will refuse
 * anyway (`activate` re-composes and 400s), and an overall discount the server
 * did not confirm is already inside `totals` leaves the amount unknowable in
 * two directions at once.
 *
 * A composition that bills NOTHING is deliberately not blocked. A tenant
 * created by `/onboard` has no locations yet — `POST /onboarding/complete` is
 * the only thing that creates them — so its plan composes to zero and warns
 * `no_locations`. That is the correct record of what was sold, and it is what
 * carries the agreed trial to the subscriptions created later.
 */
export function onboardingActivationBlock(composition: PlanComposition): string | null {
  if (composition.errors.length) {
    return `${composition.errors.length} package(s) on this plan cannot be priced (${composition.errors
      .map((e) => `${e.packageId}: ${e.reason}`)
      .join('; ')}). The plan was NOT activated — nothing is in force for this tenant.`;
  }
  if (overallDiscountUnverified(composition)) {
    return 'Billing did not confirm whether the overall discount is already inside the total, so the amount cannot be stood behind. The plan was NOT activated — nothing is in force for this tenant.';
  }
  return null;
}

export interface OnboardingPlanTotals {
  listCents: number;
  /** Everything given away — per-package AND overall, together. */
  discountCents: number;
  /** The monthly the tenant is being quoted for their first location. */
  netCents: number;
  /** How much of `discountCents` the overall discount accounts for. */
  overallDiscountCents: number;
  /** Package ids with no row in the packages catalog — a data gap, never a $0. */
  missingPackageIds: string[];
}

/**
 * The quote for a tenant that does not exist yet.
 *
 * ⚠️ WHY THIS IS NOT A SERVER PREVIEW. Everywhere else in this app a composed
 * amount comes back from `POST /plan-assignments/{tenantId}/preview`, and that
 * rule stands — but it cannot be applied HERE, because there is no tenant to
 * preview against until `CreateTenant` has run, and once it has, the tenant has
 * no locations either (only `POST /onboarding/complete` creates one). A server
 * preview at this moment would compose zero lines and answer $0.00 for every
 * plan, which is worse than useless in front of an operator quoting a price.
 *
 * So the per-package half comes from `dealPackagesTotals` — literally the same
 * shared function `super-admin-ext` runs to produce the `packagesTotals` on
 * every admin deal response — and this function does exactly ONE thing on top
 * of it: applies the overall discount to the sum, once, through the SAME
 * `applyDiscount` that `composeSubscription` applies it with. No second
 * rounding rule, no second discount model.
 *
 * It is a SINGLE-LOCATION figure, deliberately, exactly like a deal's own
 * price: what the tenant is quoted at signup, before any further store exists.
 * The real, multi-location composition is produced by the server the moment the
 * assignment is previewed against the real tenant, and that is the number that
 * governs from then on.
 */
export function onboardingPlanTotals(
  perPackage: { listCents: number; netCents: number; missingPackageIds?: string[] },
  overall?: DiscountInput,
): OnboardingPlanTotals {
  const listCents = Math.max(0, Math.round(perPackage.listCents || 0));
  const beforeOverall = Math.max(0, Math.round(perPackage.netCents || 0));
  const netCents = applyDiscount(beforeOverall, toDiscount(overall) as any);
  return {
    listCents,
    discountCents: listCents - netCents,
    netCents,
    overallDiscountCents: beforeOverall - netCents,
    missingPackageIds: perPackage.missingPackageIds || [],
  };
}
